import React, { useState, useEffect } from 'react';
import {
  X, Sparkle, CaretRight, CaretLeft, CircleNotch, Plus, Check, Trash, Lightning, CreditCard, Warning,
  Image as ImageIcon, Upload, Eye, CheckCircle,
} from '@phosphor-icons/react';
import { Select } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { formatMoney, currencySymbol } from '../utils/money';
import ConversionSetup from './ConversionSetup';

function StepIndicator({ current, total }) {
  const { t } = useTranslation();
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-400">{t('campaignDraft.stepIndicator.label', { current, total })}</span>
        <span className="text-xs text-gray-400">{Math.round((current / total) * 100)}%</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-1.5">
        <div
          className="bg-[#1D4ED8] h-1.5 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${(current / total) * 100}%` }}
        />
      </div>
    </div>
  );
}

function AiBadge() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-600 text-[10px] font-medium px-2 py-0.5 rounded-full">
      <Sparkle className="w-2.5 h-2.5" />
      {t('campaignDraft.aiBadge')}
    </span>
  );
}

function CharCounter({ current, max }) {
  const over = current > max;
  return (
    <span className={`text-[10px] tabular-nums ${over ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
      {current}/{max}
    </span>
  );
}

const MATCH_TYPE_LABELS = {
  'EXACT': 'Dokładne',
  'PHRASE': 'Fraza',
  'BROAD': 'Przybliżone',
};

export default function CampaignDraftReview({ campaign, onClose, onUpdated, trialUsed: trialUsedProp, isFree: isFreeProp }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [trialUsed, setTrialUsed] = useState(trialUsedProp || false);
  const [isFreeAccount, setIsFreeAccount] = useState(isFreeProp || false);
  const [planCatalog, setPlanCatalog] = useState(null);

  // Fetch trial status if not provided via prop (kreator kampanii renderuje ten
  // komponent bez propa). Liczy się trial_available, nie surowe trial_used:
  // klient, który zwolnił trial na innej kampanii, ma go do przeniesienia.
  // Surowa flaga pokazywała tu „Trial wykorzystany" mimo ważnego triala.
  useEffect(() => {
    if (trialUsedProp === undefined) {
      api.get('/ads/my-campaigns/')
        .then(res => {
          const dostepny = res.data.trial_available ?? !res.data.trial_used;
          setTrialUsed(!dostepny);
          setIsFreeAccount(res.data.is_free || false);
        })
        .catch(() => {});
    }
  }, [trialUsedProp]);

  // Cennik pakietów (do wyliczenia realnej ceny na karcie subskrypcji).
  useEffect(() => {
    api.get('/ads/plan-status/')
      .then(res => setPlanCatalog(res.data))
      .catch(() => {});
  }, []);
  const isPublishFailed = campaign.draft_status === 'failed' && campaign.ai_draft_data?.keywords;
  const isMetaPlatformEarly = campaign.platform === 'meta_ads';
  const _goalEarly = campaign.ai_draft_data?.campaign_goal || campaign.campaign_goal || 'traffic';
  const isLeadGenEarly = isMetaPlatformEarly && _goalEarly === 'leads';
  // Meta traffic = 7 steps; Meta leads adds a "Form" step (8); non-Meta = 6.
  const totalSteps = isMetaPlatformEarly ? (isLeadGenEarly ? 8 : 7) : 6;
  const storageKey = `draft_step_${campaign.id}`;
  const [step, setStepRaw] = useState(() => {
    if (isPublishFailed) return 'error';
    const saved = localStorage.getItem(storageKey);
    return saved ? Math.min(parseInt(saved, 10), totalSteps) : 1;
  });
  const initializedRef = React.useRef(false);
  React.useEffect(() => { initializedRef.current = true; }, []);

  const setStep = (s) => {
    // Auto-save draft when moving forward (but not on initial render)
    if (initializedRef.current && typeof step === 'number' && typeof s === 'number' && s > step && keywords.length > 0) {
      saveDraftDataQuiet();
    }
    setStepRaw(s);
    localStorage.setItem(storageKey, String(s));
  };

  // Silent save (no await, no error handling - just fire and forget)
  const saveDraftDataQuiet = () => {
    const updatedDraft = {
      ...draft,
      campaign_name: campaignName,
      business_type: businessType,
      target_audience: targetAudience,
      daily_budget: getActiveBudget(),
      campaign_goal: campaignGoal,
      ...(isLeadGen && leadForm ? { lead_form: leadForm } : {}),
      headlines: headlines.filter(h => h.trim()),
      descriptions: descriptions.filter(d => d.trim()),
      final_url: finalUrl,
      budget_strategy: budgetStrategy,
      negative_keywords: negativeKeywords.map(n => ({
        text: n.text,
        match_type: n.match_type,
        reason: n.reason,
      })),
    };
    // Preserve ad_groups structure for multi-URL campaigns
    if (hasAdGroups) {
      // Rebuild ad_groups from adGroupsState (edited headlines/descriptions/urls) + keywords
      const adGroupMap = {};
      adGroupsState.forEach(ag => {
        adGroupMap[ag.name] = {
          name: ag.name,
          final_url: ag.final_url,
          headlines: ag.headlines,
          descriptions: ag.descriptions,
          keywords: [],
        };
      });
      keywords.forEach(kw => {
        const groupName = kw.ad_group_name;
        if (adGroupMap[groupName]) {
          adGroupMap[groupName].keywords.push({
            text: kw.text, match_type: kw.match_type,
            estimated_cpc: kw.estimated_cpc, relevance: kw.relevance,
          });
        }
      });
      updatedDraft.ad_groups = Object.values(adGroupMap);
    } else {
      updatedDraft.keywords = keywords.map(k => ({
        text: k.text, match_type: k.match_type,
        estimated_cpc: k.estimated_cpc, relevance: k.relevance,
      }));
    }
    api.post(`/ads/drafts/${campaign.id}/update/`, {
      name: campaignName,
      budget_amount: getActiveBudget(),
      bidding_strategy: biddingStrategy,
      draft_data: updatedDraft,
    }).catch(() => {});
  };

  const draft = campaign.ai_draft_data || {};
  const curSym = currencySymbol(campaign.currency);
  const hasAdGroups = Array.isArray(draft.ad_groups) && draft.ad_groups.length > 0;

  // For multi-URL campaigns, flatten ad_groups into keywords/headlines/descriptions
  // while preserving group info for display
  const _flatKeywords = [];
  const _flatHeadlines = [];
  const _flatDescriptions = [];
  if (hasAdGroups) {
    draft.ad_groups.forEach(ag => {
      (ag.keywords || []).forEach(kw => {
        _flatKeywords.push({ ...kw, ad_group_name: ag.name, final_url: ag.final_url });
      });
      (ag.headlines || []).forEach(h => {
        if (!_flatHeadlines.includes(h)) _flatHeadlines.push(h);
      });
      (ag.descriptions || []).forEach(d => {
        if (!_flatDescriptions.includes(d)) _flatDescriptions.push(d);
      });
    });
  }

  // Step 1: AI review
  const [campaignName, setCampaignName] = useState(draft.campaign_name || campaign.name || '');
  const [businessType, setBusinessType] = useState(draft.business_type || '');
  const [targetAudience, setTargetAudience] = useState(draft.target_audience || '');

  // Step 2: Budget + bidding strategy
  const [budgetStrategy, setBudgetStrategy] = useState(draft.budget_strategy || 'ai');
  const [customBudget, setCustomBudget] = useState(String(draft.daily_budget || '50'));
  const aiBudget = draft.daily_budget || 50;
  const budgetReason = draft.budget_reason || '';
  const [biddingStrategy, setBiddingStrategy] = useState(
    campaign.bidding_strategy || draft.recommended_strategy || 'manual_cpc'
  );

  // Step 3: Keywords (from ad_groups or flat)
  const rawKeywords = hasAdGroups ? _flatKeywords : (draft.keywords || []);
  const [keywords, setKeywords] = useState(
    rawKeywords.map((kw, i) => ({
      id: i,
      text: kw.text || kw.keyword || '',
      match_type: kw.match_type || 'PHRASE',
      estimated_cpc: kw.estimated_cpc || null,
      relevance: kw.relevance || kw.trafnosc || 'Wysoka',
      ad_group_name: kw.ad_group_name || '',
      final_url: kw.final_url || '',
    }))
  );
  // Wykluczenia zaproponowane przez AI. Kampanie startowały bez żadnych, więc
  // płaciły za zapytania z innej intencji (darmowe wzory, praca, inne znaczenie frazy).
  const [negativeKeywords, setNegativeKeywords] = useState(
    (campaign.ai_draft_data?.negative_keywords || []).map((n, i) => ({
      id: `neg-${i}`,
      text: n.text || '',
      match_type: n.match_type || 'BROAD',
      reason: n.reason || '',
    }))
  );
  const [newNegative, setNewNegative] = useState('');

  const [newKeyword, setNewKeyword] = useState('');

  // Step 4: Ad copy
  // For multi-URL: keep per-group state; for single: flat
  const [adGroupsState, setAdGroupsState] = useState(
    hasAdGroups
      ? draft.ad_groups.map((ag, i) => ({
          id: i,
          name: ag.name || '',
          final_url: ag.final_url || '',
          headlines: ag.headlines || [],
          descriptions: ag.descriptions || [],
        }))
      : []
  );
  const [headlines, setHeadlines] = useState(hasAdGroups ? _flatHeadlines : (draft.headlines || []));
  const [descriptions, setDescriptions] = useState(hasAdGroups ? _flatDescriptions : (draft.descriptions || []));
  const [finalUrl, setFinalUrl] = useState(campaign.source_url || '');
  const [activeAdGroup, setActiveAdGroup] = useState(0);

  // Keyword strategy
  const [keywordStrategy, setKeywordStrategy] = useState(draft.keyword_strategy || campaign.keyword_strategy || 'standard');
  const [regeneratingKw, setRegeneratingKw] = useState(false);
  const [originalKeywords, setOriginalKeywords] = useState(null); // saved before budget switch
  const [budgetKwCount, setBudgetKwCount] = useState('20'); // how many long-tail keywords to generate

  // Meta Ads audience data
  const isMetaPlatform = campaign.platform === 'meta_ads';
  const [audience, setAudience] = useState(draft.audience || {});
  const [primaryText, setPrimaryText] = useState(draft.primary_text || '');
  const goalFromDraft = draft.campaign_goal || campaign.campaign_goal || 'traffic';
  const [ctaButton, setCtaButton] = useState(draft.cta_button || (goalFromDraft === 'leads' ? 'SIGN_UP' : 'LEARN_MORE'));
  const [campaignGoal] = useState(goalFromDraft);

  // Lead-gen form (Meta leads only). Generated by AI, editable here before publish.
  const [leadForm, setLeadForm] = useState(draft.lead_form || null);
  const [leadFormLoading, setLeadFormLoading] = useState(false);
  const [leadFormFetched, setLeadFormFetched] = useState(!!draft.lead_form);

  // Sync audience state when campaign prop updates (e.g. after fresh fetch from server)
  const prevDraftIdRef = React.useRef(JSON.stringify((draft.audience?.interests_data || []).map(i => i.id)));
  useEffect(() => {
    if (!draft.audience) return;
    const newIds = JSON.stringify((draft.audience.interests_data || []).map(i => i.id));
    if (newIds !== prevDraftIdRef.current) {
      prevDraftIdRef.current = newIds;
      setAudience(draft.audience);
    }
  }, [draft.audience]);
  const creativeSuggestions = draft.creative_suggestions || [];
  // Meta interest search
  const [interestQuery, setInterestQuery] = useState('');
  const [interestResults, setInterestResults] = useState([]);
  const [interestLoading, setInterestLoading] = useState(false);
  const [audienceSize, setAudienceSize] = useState(null);
  const [audienceSizeLoading, setAudienceSizeLoading] = useState(false);
  const interestSearchTimer = React.useRef(null);

  // Facebook Page selection (Meta Ads)
  const [fbPages, setFbPages] = useState([]);
  const [selectedPageId, setSelectedPageId] = useState('');
  const [fbPagesLoading, setFbPagesLoading] = useState(false);

  // Creative images (Meta Ads)
  const [creativeImages, setCreativeImages] = useState([]);
  const [creativeLoading, setCreativeLoading] = useState(false);
  const [creativeUploading, setCreativeUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [creativesFetched, setCreativesFetched] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiGenerateError, setAiGenerateError] = useState('');

  // Creative videos (Meta Ads: Stories/Reels/feed)
  const [creativeVideos, setCreativeVideos] = useState([]);
  const [videoUploading, setVideoUploading] = useState(false);

  // Audience size fetcher (used by step 3 and auto-validate)
  const fetchAudienceSize = async (aud) => {
    const interests = (aud || audience).interests_data || [];
    const validInterests = interests.filter(i => i.id && !String(i.id).startsWith('ai_'));
    if (validInterests.length === 0) { setAudienceSize(null); return; }
    const geo = (draft.reach || 'national') === 'international' ? '' : 'PL';
    setAudienceSizeLoading(true);
    try {
      const res = await api.post('/ads/meta/audience-size/', {
        targeting: {
          age_min: (aud || audience).age_min || 18,
          age_max: (aud || audience).age_max || 55,
          genders: (aud || audience).genders || ['all'],
          interests: validInterests.map(i => ({ id: i.id, name: i.name })),
          geo: geo || 'PL',
        },
      });
      setAudienceSize(res.data.estimate);
    } catch { /* silently handle */ }
    setAudienceSizeLoading(false);
  };

  // Auto-validate AI interests (ai_ prefixed) when entering step 3
  const autoValidateRef = React.useRef(false);
  useEffect(() => {
    if (step !== 3 || !isMetaPlatform) return;
    if (autoValidateRef.current) return;
    const aiOnly = (audience.interests_data || []).filter(i => String(i.id).startsWith('ai_'));
    if (aiOnly.length === 0) return;
    autoValidateRef.current = true;

    (async () => {
      const validated = [];
      const seenIds = new Set();
      // Keep already-validated interests
      for (const interest of (audience.interests_data || [])) {
        if (!String(interest.id).startsWith('ai_')) {
          seenIds.add(interest.id);
          validated.push(interest);
        }
      }
      for (const interest of aiOnly) {
        try {
          const res = await api.get(`/ads/meta/interests/search/?q=${encodeURIComponent(interest.name)}`);
          const matches = res.data.interests || [];
          if (matches.length > 0) {
            const best = matches.find(m => m.name.toLowerCase() === interest.name.toLowerCase()) || matches[0];
            if (!seenIds.has(best.id)) {
              seenIds.add(best.id);
              validated.push({ ...best, ai_suggested: true });
            }
          }
        } catch { /* skip */ }
      }
      if (validated.length > 0) {
        const updated = { ...audience, interests_data: validated };
        setAudience(updated);
      }
    })();
  }, [step, isMetaPlatform]); // eslint-disable-line

  // Fetch audience size when entering step 3 or when audience changes
  const audienceFetchedRef = React.useRef(false);
  useEffect(() => {
    if (step !== 3 || !isMetaPlatform) return;
    const validInterests = (audience.interests_data || []).filter(i => i.id && !String(i.id).startsWith('ai_'));
    if (validInterests.length > 0 && !audienceFetchedRef.current) {
      audienceFetchedRef.current = true;
      fetchAudienceSize(audience);
    }
  }, [step, isMetaPlatform, audience]); // eslint-disable-line

  // Fetch Facebook Pages when entering creative step (step 5 for Meta)
  useEffect(() => {
    if (step === 5 && isMetaPlatform && fbPages.length === 0 && !fbPagesLoading) {
      setFbPagesLoading(true);
      api.get('/ads/meta/pages/')
        .then(res => {
          setFbPages(res.data.pages || []);
          setSelectedPageId(res.data.selected_page_id || (res.data.pages?.[0]?.id) || '');
        })
        .catch(() => {})
        .finally(() => setFbPagesLoading(false));
    }
  }, [step, isMetaPlatform]); // eslint-disable-line

  // Auto-fetch creative images when entering the creative step
  useEffect(() => {
    if (step === 5 && isMetaPlatform && !creativesFetched && !creativeLoading) {
      setCreativeLoading(true);
      Promise.all([
        api.get(`/ads/drafts/${campaign.id}/images/`),
        api.get(`/ads/drafts/${campaign.id}/videos/`).catch(() => ({ data: { videos: [] } })),
      ])
        .then(([imgRes, vidRes]) => {
          setCreativeImages(imgRes.data.images || []);
          setCreativeVideos(vidRes.data.videos || []);
          setCreativesFetched(true);
        })
        .catch(() => {})
        .finally(() => setCreativeLoading(false));
    }
  }, [step, isMetaPlatform, creativesFetched, creativeLoading, campaign.id]);

  // Step mapping: Meta has an extra "Creative" step 5, and lead-gen campaigns
  // add a "Form" step 6 (so summary/activate shift by one).
  const isLeadGen = isMetaPlatform && campaignGoal === 'leads';
  const stepCreative = isMetaPlatform ? 5 : null;
  const stepLeadForm = isLeadGen ? 6 : null;
  const stepSummary = isMetaPlatform ? (isLeadGen ? 7 : 6) : 5;
  const stepActivate = isMetaPlatform ? (isLeadGen ? 8 : 7) : 6;

  // Standard prefilled Meta lead fields available to toggle on/off.
  const LEAD_FIELD_LABELS = {
    full_name: t('campaignDraft.leadFields.full_name'), email: t('campaignDraft.leadFields.email'), phone: t('campaignDraft.leadFields.phone'),
    company_name: t('campaignDraft.leadFields.company_name'), job_title: t('campaignDraft.leadFields.job_title'), city: t('campaignDraft.leadFields.city'),
    first_name: t('campaignDraft.leadFields.first_name'), last_name: t('campaignDraft.leadFields.last_name'),
  };

  // Auto-generate (or load) the lead form when entering the Form step.
  useEffect(() => {
    if (step === stepLeadForm && isLeadGen && !leadFormFetched && !leadFormLoading) {
      setLeadFormLoading(true);
      api.get(`/ads/drafts/${campaign.id}/lead-form/`)
        .then(res => { setLeadForm(res.data.lead_form); setLeadFormFetched(true); })
        .catch(() => {})
        .finally(() => setLeadFormLoading(false));
    }
  }, [step, stepLeadForm, isLeadGen, leadFormFetched, leadFormLoading, campaign.id]);

  // Lead-form editing helpers
  const lf = leadForm || {};
  const lfFields = lf.fields || [];
  const lfQuestions = lf.custom_questions || [];
  const patchLeadForm = (patch) => setLeadForm({ ...(leadForm || {}), ...patch });
  const toggleLeadField = (key) => {
    const has = lfFields.includes(key);
    // Always keep at least one field.
    if (has && lfFields.length <= 1) return;
    patchLeadForm({ fields: has ? lfFields.filter(f => f !== key) : [...lfFields, key] });
  };
  const updateQuestion = (i, patch) =>
    patchLeadForm({ custom_questions: lfQuestions.map((q, idx) => idx === i ? { ...q, ...patch } : q) });
  const removeQuestion = (i) =>
    patchLeadForm({ custom_questions: lfQuestions.filter((_, idx) => idx !== i) });
  const addQuestion = () => {
    if (lfQuestions.length >= 3) return;
    patchLeadForm({ custom_questions: [...lfQuestions, { label: '', key: `q${lfQuestions.length + 1}`, options: [] }] });
  };
  const regenerateLeadForm = () => {
    setLeadFormLoading(true);
    api.post(`/ads/drafts/${campaign.id}/lead-form/`, { regenerate: true })
      .then(res => { setLeadForm(res.data.lead_form); })
      .catch(() => {})
      .finally(() => setLeadFormLoading(false));
  };

  // Step 5/6: Publishing
  const [publishing, setPublishing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [publishDone, setPublishDone] = useState(false);
  const [publishWarnings, setPublishWarnings] = useState([]);
  const [showConversionSetup, setShowConversionSetup] = useState(false);
  const [conversionDone, setConversionDone] = useState(false);
  const [publishError, setPublishError] = useState('');

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  // Compute active budget
  const getActiveBudget = () => {
    if (budgetStrategy === 'test') return 5;
    if (budgetStrategy === 'recommended') return aiBudget;
    return parseFloat(customBudget) || 1;
  };

  const estimatedMonthlyCost = (getActiveBudget() * 30).toFixed(0);

  // Rekomendowany pakiet dla tej kampanii (najtańszy mieszczący budżet + liczbę
  // kampanii) - żeby karta subskrypcji pokazała realną cenę (np. Micro 79 zł), nie 299.
  const recommendedPlan = (() => {
    const ps = planCatalog;
    if (!ps || !ps.plans || !ps.plans.length) return null;
    const daily = getActiveBudget();
    const need = (ps.managed_campaigns || 0) + 1;
    return ps.plans.find(p =>
      (p.campaigns == null || need <= p.campaigns) &&
      (!p.budget_max_daily || daily <= p.budget_max_daily)
    ) || ps.plans[ps.plans.length - 1];
  })();
  const subscriptionPriceLabel = recommendedPlan
    ? t('campaignDraft.activate.subscriptionPrice', { price: recommendedPlan.price })
    : t('campaignDraft.activate.subscriptionPriceFallback');

  // Keywords helpers
  const [addToGroup, setAddToGroup] = useState(hasAdGroups && draft.ad_groups?.length > 0 ? draft.ad_groups[0].name : '');

  const addCustomKeyword = () => {
    if (!newKeyword.trim()) return;
    const exists = keywords.some(k => k.text.toLowerCase() === newKeyword.trim().toLowerCase());
    if (exists) return;
    setKeywords(prev => [
      ...prev,
      {
        id: Date.now(), text: newKeyword.trim(), match_type: 'PHRASE',
        estimated_cpc: null, relevance: 'Wysoka',
        ad_group_name: hasAdGroups ? addToGroup : '',
        final_url: hasAdGroups ? (draft.ad_groups?.find(ag => ag.name === addToGroup)?.final_url || '') : '',
      },
    ]);
    setNewKeyword('');
  };

  const removeKeyword = (id) => {
    setKeywords(prev => prev.filter(k => k.id !== id));
  };

  const updateKeywordMatchType = (id, mt) => {
    setKeywords(prev => prev.map(k => k.id === id ? { ...k, match_type: mt } : k));
  };

  const removeNegative = (id) => {
    setNegativeKeywords(prev => prev.filter(n => n.id !== id));
  };

  const addNegative = () => {
    const text = newNegative.trim().toLowerCase();
    if (!text) return;
    if (negativeKeywords.some(n => n.text === text)) { setNewNegative(''); return; }
    setNegativeKeywords(prev => [
      ...prev,
      { id: `neg-${Date.now()}`, text, match_type: text.includes(' ') ? 'PHRASE' : 'BROAD', reason: '' },
    ]);
    setNewNegative('');
  };

  // Headlines/descriptions helpers
  const updateHeadline = (i, v) => setHeadlines(prev => prev.map((h, idx) => idx === i ? v : h));
  const removeHeadline = (i) => { if (headlines.length > 1) setHeadlines(prev => prev.filter((_, idx) => idx !== i)); };
  const addHeadline = () => { if (headlines.length < 15) setHeadlines(prev => [...prev, '']); };

  const updateDescription = (i, v) => setDescriptions(prev => prev.map((d, idx) => idx === i ? v : d));
  const removeDescription = (i) => { if (descriptions.length > 1) setDescriptions(prev => prev.filter((_, idx) => idx !== i)); };
  const addDescription = () => { if (descriptions.length < 4) setDescriptions(prev => [...prev, '']); };

  // Delete draft
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/ads/drafts/${campaign.id}/delete/`);
      if (onUpdated) onUpdated();
      handleClose();
    } catch {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Save draft data (used before activation in step 6)
  const saveDraftData = async () => {
    const updatedDraft = {
      ...draft,
      campaign_name: campaignName,
      business_type: businessType,
      target_audience: targetAudience,
      daily_budget: getActiveBudget(),
      campaign_goal: campaignGoal,
      ...(isLeadGen && leadForm ? { lead_form: leadForm } : {}),
      keywords: keywords.map(k => ({
        text: k.text,
        match_type: k.match_type,
        estimated_cpc: k.estimated_cpc,
        relevance: k.relevance,
      })),
      headlines: headlines.filter(h => h.trim()),
      descriptions: descriptions.filter(d => d.trim()),
      final_url: finalUrl,
      budget_strategy: budgetStrategy,
    };

    await api.post(`/ads/drafts/${campaign.id}/update/`, {
      name: campaignName,
      budget_amount: getActiveBudget(),
      bidding_strategy: biddingStrategy,
      draft_data: updatedDraft,
    });
  };

  // Activate trial and publish
  const pollPublishStatus = async (campaignId, maxAttempts = 15) => {
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const res = await api.get(`/ads/drafts/${campaignId}/status/`);
        const ds = res.data?.draft_status;
        if (ds === 'published') return 'published';
        if (ds === 'failed') return 'failed';
      } catch { /* keep polling */ }
    }
    return 'timeout';
  };

  // Kampania powstała w Google Ads, ale Google mógł odrzucić część elementów
  // (np. reklamę za zasady). Pokazujemy powody, żeby klient wiedział, co poprawić.
  const loadPublishWarnings = async () => {
    try {
      const res = await api.get(`/ads/drafts/${campaign.id}/status/`);
      setPublishWarnings(res.data?.ai_draft_data?.publish_warnings || []);
    } catch { setPublishWarnings([]); }
  };

  const handleActivateTrial = async () => {
    setPublishing(true);
    setPublishError('');
    try {
      await saveDraftData();
      await api.post(`/ads/drafts/${campaign.id}/publish/`, {
        activation_type: 'trial',
      });
      // Poll for actual publish result (Celery task runs async)
      const result = await pollPublishStatus(campaign.id);
      if (result === 'published') {
        await loadPublishWarnings();
        setPublishDone(true);
      } else {
        const statusRes = await api.get(`/ads/drafts/${campaign.id}/status/`);
        const errMsg = statusRes.data?.ai_draft_data?.error_message || t('campaignDraft.messages.publishFailed');
        setPublishError(errMsg);
      }
      if (onUpdated) onUpdated();
    } catch (err) {
      setPublishError(err.response?.data?.error || t('campaignDraft.messages.activationError'));
    } finally {
      setPublishing(false);
    }
  };

  // Start subscription checkout
  const handleSubscribe = async () => {
    setPublishing(true);
    setPublishError('');
    try {
      await saveDraftData();
      const res = await api.post(`/ads/drafts/${campaign.id}/publish/`, {
        activation_type: 'subscription',
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      setPublishError(err.response?.data?.error || t('campaignDraft.messages.checkoutError'));
      setPublishing(false);
    }
  };

  // Retry publish for failed campaigns
  const handleRegenerateAdCopy = async () => {
    setRegenerating(true);
    try {
      const keywordTexts = keywords.map(k => k.text).join(', ');
      const res = await api.post(`/ai/generate-copy/`, {
        product_info: {
          name: campaignName,
          description: keywordTexts,
          url: finalUrl || campaign.source_url,
        },
        target_audience: targetAudience || '',
      });
      const data = res.data;
      if (data.headlines && data.headlines.length > 0) {
        setHeadlines(data.headlines.slice(0, 15));
      }
      if (data.descriptions && data.descriptions.length > 0) {
        setDescriptions(data.descriptions.slice(0, 4));
      }
    } catch {
      // silently fail
    } finally {
      setRegenerating(false);
    }
  };

  const handleRetryPublish = async () => {
    setPublishing(true);
    setPublishError('');
    try {
      await api.post(`/ads/drafts/${campaign.id}/update/`, {
        name: campaignName,
        budget_amount: getActiveBudget(),
        draft_data: campaign.ai_draft_data,
      });
      const activationType = !trialUsed ? 'trial' : 'subscription';
      const res = await api.post(`/ads/drafts/${campaign.id}/publish/`, {
        activation_type: activationType,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      const result = await pollPublishStatus(campaign.id);
      if (result === 'published') {
        await loadPublishWarnings();
        setPublishDone(true);
        setStepRaw('success');
      } else {
        const statusRes = await api.get(`/ads/drafts/${campaign.id}/status/`);
        setPublishError(statusRes.data?.ai_draft_data?.error_message || t('campaignDraft.messages.publishFailed'));
      }
      if (onUpdated) onUpdated();
    } catch (err) {
      setPublishError(err.response?.data?.error || t('campaignDraft.messages.publishError'));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className={`absolute inset-0 bg-black/20 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />

      <div
        className={`relative w-full md:w-[640px] bg-white h-full shadow-[-8px_0_30px_rgba(0,0,0,0.15)] transition-transform duration-300 flex flex-col ${
          visible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 h-[57px] flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
              <Sparkle className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">{t('campaignDraft.header.title')}</h2>
              <p className="text-xs text-gray-400">{t('campaignDraft.header.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="text-gray-300 hover:text-red-500 transition p-1"
              title={t('campaignDraft.header.deleteTooltip')}
            >
              <Trash className="w-4 h-4" />
            </button>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 relative">
          {/* Full overlay when regenerating keywords */}
          {regeneratingKw && (
            <div className="absolute inset-0 z-20 bg-white/90 backdrop-blur-sm flex flex-col items-center justify-center gap-3">
              <div className="relative">
                <img src="/logo_icon_only.svg" alt="" className="w-10 h-10 animate-pulse" />
              </div>
              <p className="text-sm font-medium text-gray-700">{t('campaignDraft.regenOverlay.title')}</p>
              <p className="text-xs text-gray-400">{t('campaignDraft.regenOverlay.subtitle')}</p>
            </div>
          )}
          {typeof step === 'number' && <StepIndicator current={step} total={totalSteps} />}

          {/* Szkic po nieudanej publikacji: powód widoczny podczas poprawiania ustawień. */}
          {typeof step === 'number' && (campaign.ai_draft_data?.last_error_message || (campaign.draft_status === 'failed' && (campaign.ai_draft_data?.error_message || campaign.draft_error))) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-xs font-medium text-red-800">{t('campaignDraft.errorState.lastErrorTitle')}</p>
              <p className="text-xs text-red-600 mt-0.5">{campaign.ai_draft_data?.last_error_message || campaign.ai_draft_data?.error_message || campaign.draft_error}</p>
            </div>
          )}

          {/* ===== STEP 1: Przegląd sugestii AI ===== */}
          {step === 1 && (
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">{t('campaignDraft.step1.title')}</h3>
              <p className="text-sm text-gray-500 mb-6">
                {t('campaignDraft.step1.subtitle')}
              </p>

              <div className="space-y-5">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-gray-600">{t('campaignDraft.step1.campaignNameLabel')}</label>
                    <AiBadge />
                  </div>
                  <input
                    type="text"
                    value={campaignName}
                    onChange={e => setCampaignName(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-gray-600">{t('campaignDraft.step1.businessTypeLabel')}</label>
                    <AiBadge />
                  </div>
                  <input
                    type="text"
                    value={businessType}
                    onChange={e => setBusinessType(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-gray-600">{t('campaignDraft.step1.targetAudienceLabel')}</label>
                    <AiBadge />
                  </div>
                  <textarea
                    value={targetAudience}
                    onChange={e => setTargetAudience(e.target.value)}
                    rows={3}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                  />
                </div>

                <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
                  <p className="text-xs text-gray-500">{t('campaignDraft.step1.analyzedPage')}</p>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">{campaign.source_url}</p>
                </div>

                {/* Market data & forecasts (Google Ads only - keyword planner data) */}
                {draft.market_data && !isMetaPlatform && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <p className="text-xs font-medium text-gray-600 mb-3 uppercase tracking-wider">{t('campaignDraft.step1.marketAnalysis')}</p>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div className="bg-indigo-50 rounded-lg p-2.5">
                        <p className="text-[10px] text-indigo-400 uppercase">{t('campaignDraft.step1.searchesPerMonth')}</p>
                        <p className="text-sm font-semibold text-indigo-700">{(draft.market_data.total_monthly_searches || 0).toLocaleString('pl-PL')}</p>
                      </div>
                      <div className="bg-indigo-50 rounded-lg p-2.5">
                        <p className="text-[10px] text-indigo-400 uppercase">{t('campaignDraft.step1.avgCpc')}</p>
                        <p className="text-sm font-semibold text-indigo-700">{draft.market_data.avg_cpc} {curSym}</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-2.5">
                        <p className="text-[10px] text-green-500 uppercase">{t('campaignDraft.step1.forecastClicks')}</p>
                        <p className="text-sm font-semibold text-green-700">{(draft.market_data.estimated_monthly_clicks || 0).toLocaleString('pl-PL')}</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-2.5">
                        <p className="text-[10px] text-green-500 uppercase">{t('campaignDraft.step1.forecastConversions')}</p>
                        <p className="text-sm font-semibold text-green-700">~{draft.market_data.estimated_conversions}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className={`inline-block w-2 h-2 rounded-full ${draft.market_data.competition_level === 'Wysoka' ? 'bg-red-400' : draft.market_data.competition_level === 'Średnia' ? 'bg-yellow-400' : 'bg-green-400'}`} />
                      <span className="text-gray-600">{t('campaignDraft.step1.competitionLabel')} <strong>{draft.market_data.competition_level}</strong></span>
                      <span className="text-gray-400 ml-auto">{t('campaignDraft.step1.costPerMonth', { cost: draft.market_data.estimated_monthly_cost, cur: curSym })}</span>
                    </div>
                  </div>
                )}

                {/* Competitors */}
                {draft.competitors && draft.competitors.length > 0 && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <p className="text-xs font-medium text-gray-600 mb-3 uppercase tracking-wider">{isMetaPlatform ? t('campaignDraft.step1.competitorsMeta') : t('campaignDraft.step1.competitorsGoogle')}</p>
                    <div className="space-y-2">
                      {draft.competitors.map((comp, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${comp.strength === 'silny' ? 'bg-red-400' : comp.strength === 'średni' ? 'bg-yellow-400' : 'bg-green-400'}`} />
                          <span className="text-sm font-medium text-gray-800 flex-shrink-0">{comp.name}</span>
                          <span className="text-xs text-gray-400 truncate">{comp.note}</span>
                        </div>
                      ))}
                    </div>
                    {draft.market_summary && (
                      <p className="text-xs text-gray-500 mt-3 leading-relaxed border-t border-gray-100 pt-2">{draft.market_summary}</p>
                    )}
                  </div>
                )}

                {/* Strategy recommendation - moved to step 2 as editable selector */}
              </div>

            </div>
          )}

          {/* ===== STEP 2: Strategia budżetu ===== */}
          {step === 2 && (
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">{t('campaignDraft.step2.title')}</h3>
              <p className="text-sm text-gray-500 mb-4">
                {t('campaignDraft.step2.subtitle')}
              </p>

              {/* Campaign goal info - Meta only (goal already selected in wizard) */}
              {isMetaPlatform && campaignGoal && (
                <div className="mb-5 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span>{campaignGoal === 'leads' ? '📋' : '🌐'}</span>
                    <span className="text-sm font-medium text-indigo-800">
                      {campaignGoal === 'leads' ? t('campaignDraft.step2.goalLeads') : t('campaignDraft.step2.goalTraffic')}
                    </span>
                  </div>
                  {campaignGoal === 'leads' && (
                    <p className="text-xs text-indigo-600">
                      {t('campaignDraft.step2.goalLeadsDesc')}
                    </p>
                  )}
                </div>
              )}

              {draft.market_data && !isMetaPlatform && (
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 mb-4">
                  <p className="text-[10px] text-gray-400 uppercase mb-1">{t('campaignDraft.step2.marketBasis')}</p>
                  <p className="text-xs text-gray-600">
                    {t('campaignDraft.step2.marketCpcPrefix')} <strong>{draft.market_data.avg_cpc} {curSym}</strong>.{' '}
                    {t('campaignDraft.step2.marketBudgetHint', {
                      budget: aiBudget,
                      cur: curSym,
                      clicks: Math.round(aiBudget / Math.max(draft.market_data.avg_cpc, 0.5)),
                      conversions: Math.round(aiBudget / Math.max(draft.market_data.avg_cpc, 0.5) * 30 * 0.03),
                    })}
                  </p>
                </div>
              )}

              <div className="space-y-3">
                {/* Option: Test */}
                <button
                  onClick={() => setBudgetStrategy('test')}
                  className={`w-full text-left border rounded-lg p-4 transition ${
                    budgetStrategy === 'test'
                      ? 'border-indigo-600 bg-indigo-50 ring-1 ring-indigo-600'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-gray-900">{t('campaignDraft.step2.testTitle')}</span>
                    <span className="text-sm font-bold text-indigo-600">{t('campaignDraft.units.perDay', { amount: 5, cur: curSym })}</span>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {t('campaignDraft.step2.testDesc')}
                  </p>
                </button>

                {/* Option: Recommended */}
                <button
                  onClick={() => setBudgetStrategy('recommended')}
                  className={`w-full text-left border rounded-lg p-4 transition ${
                    budgetStrategy === 'recommended'
                      ? 'border-indigo-600 bg-indigo-50 ring-1 ring-indigo-600'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">{t('campaignDraft.step2.recommendedTitle')}</span>
                      <AiBadge />
                    </div>
                    <span className="text-sm font-bold text-indigo-600">{t('campaignDraft.units.perDay', { amount: aiBudget, cur: curSym })}</span>
                  </div>
                  {budgetReason && (
                    <p className="text-xs text-gray-500 leading-relaxed">{budgetReason}</p>
                  )}
                </button>

                {/* Option: Custom */}
                <button
                  onClick={() => setBudgetStrategy('custom')}
                  className={`w-full text-left border rounded-lg p-4 transition ${
                    budgetStrategy === 'custom'
                      ? 'border-indigo-600 bg-indigo-50 ring-1 ring-indigo-600'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-gray-900">{t('campaignDraft.step2.customTitle')}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{t('campaignDraft.step2.customDesc')}</p>
                  {budgetStrategy === 'custom' && (
                    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                      <input
                        type="number"
                        value={customBudget}
                        onChange={e => { const v = Math.max(1, parseInt(e.target.value) || 1); setCustomBudget(String(v)); }}
                        min="1"
                        step="1"
                        className="w-32 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder={t('campaignDraft.step2.customPlaceholder')}
                      />
                      <span className="text-xs text-gray-500">{t('campaignDraft.units.perDaySuffix', { cur: curSym })}</span>
                    </div>
                  )}
                </button>
              </div>

              {/* Bidding strategy (Google Ads only) */}
              {!isMetaPlatform && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">{t('campaignDraft.step2.biddingTitle')}</h4>
                  <p className="text-xs text-gray-500 mb-2">{t('campaignDraft.step2.biddingSubtitle')}</p>
                  <div className="space-y-1.5">
                    {[
                      { value: 'manual_cpc', label: t('campaignDraft.step2.bidding.manualLabel'), desc: t('campaignDraft.step2.bidding.manualDesc') },
                      { value: 'maximize_clicks', label: t('campaignDraft.step2.bidding.maxClicksLabel'), desc: t('campaignDraft.step2.bidding.maxClicksDesc') },
                      { value: 'maximize_conversions', label: t('campaignDraft.step2.bidding.maxConversionsLabel'), desc: t('campaignDraft.step2.bidding.maxConversionsDesc') },
                      { value: 'target_cpa', label: t('campaignDraft.step2.bidding.targetCpaLabel'), desc: t('campaignDraft.step2.bidding.targetCpaDesc') },
                    ].map(opt => {
                      const isSel = biddingStrategy === opt.value;
                      const isRec = draft.recommended_strategy === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setBiddingStrategy(opt.value)}
                          className={`w-full text-left border rounded-lg px-3 py-2.5 transition ${
                            isSel ? 'border-indigo-500 bg-indigo-50/50 ring-1 ring-indigo-200' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`text-sm font-medium ${isSel ? 'text-indigo-700' : 'text-gray-800'}`}>{opt.label}</p>
                            {isRec && <span className="text-[10px] font-semibold text-indigo-700 bg-indigo-100 rounded px-1.5 py-0.5">{t('campaignDraft.step2.biddingRecommended')}</span>}
                          </div>
                          <p className="text-[11px] text-gray-500 mt-0.5">{opt.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                  {draft.strategy_reason && (
                    <p className="text-[11px] text-gray-500 mt-2">{draft.strategy_reason}</p>
                  )}
                </div>
              )}

              {/* Monthly estimate */}
              <div className="mt-5 bg-gray-50 border border-gray-100 rounded-lg p-3">
                <p className="text-xs text-gray-500">{t('campaignDraft.step2.monthlyEstimate')}</p>
                <p className="text-lg font-bold text-gray-900">{formatMoney(estimatedMonthlyCost, campaign.currency, { decimals: 0 })}</p>
                <p className="text-xs text-gray-400">{t('campaignDraft.step2.monthlyBasis', { budget: getActiveBudget(), cur: curSym })}</p>
              </div>

              <div className="mt-4 bg-indigo-50 border border-indigo-100 rounded-lg p-3">
                <p className="text-xs text-indigo-700 font-medium mb-1">{t('campaignDraft.step2.startTitle')}</p>
                <p className="text-xs text-indigo-600">
                  {t('campaignDraft.step2.startText')}
                </p>
              </div>
            </div>
          )}

          {/* ===== STEP 3: Keywords (Google) or Audience (Meta) ===== */}
          {step === 3 && isMetaPlatform && (() => {
            const searchInterests = (q) => {
              if (interestSearchTimer.current) clearTimeout(interestSearchTimer.current);
              setInterestQuery(q);
              if (q.length < 2) { setInterestResults([]); return; }
              interestSearchTimer.current = setTimeout(async () => {
                setInterestLoading(true);
                try {
                  const res = await api.get(`/ads/meta/interests/search/?q=${encodeURIComponent(q)}`);
                  setInterestResults(res.data.interests || []);
                } catch { setInterestResults([]); }
                setInterestLoading(false);
              }, 300);
            };

            const addInterest = (interest) => {
              const current = audience.interests_data || [];
              if (current.some(i => i.id === interest.id)) return;
              const updated = { ...audience, interests_data: [...current, interest] };
              setAudience(updated);
              setInterestQuery('');
              setInterestResults([]);
              fetchAudienceSize(updated);
            };

            const removeInterest = (id) => {
              const updated = { ...audience, interests_data: (audience.interests_data || []).filter(i => i.id !== id) };
              setAudience(updated);
              fetchAudienceSize(updated);
            };

            const campaignReach = draft.reach || 'national';
            const geoCode = campaignReach === 'international' ? '' : 'PL';
            const geoLabel = campaignReach === 'international' ? t('campaignDraft.audience.geoGlobal') : t('campaignDraft.audience.geoPoland');

            const formatNum = (n) => {
              if (!n) return '0';
              if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
              if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
              return String(n);
            };

            // Migrate AI-generated string interests to interests_data if backend didn't validate
            if (!audience.interests_data && audience.interests?.length > 0) {
              const migrated = audience.interests.map((name, i) => ({ id: `ai_${i}`, name, audience_size: 0, ai_suggested: true }));
              audience.interests_data = migrated;
            }

            const allInterests = audience.interests_data || [];

            return (
            <div>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-base font-semibold text-gray-900">{t('campaignDraft.audience.title')}</h3>
                {audienceSizeLoading ? (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-400 px-2.5 py-1">
                    <CircleNotch size={12} className="animate-spin" /> {t('campaignDraft.audience.estimating')}
                  </span>
                ) : audienceSize && audienceSize.users_upper_bound > 0 ? (
                  <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${
                    audienceSize.users_upper_bound > 1_000_000 ? 'bg-green-50 text-green-700 border border-green-200' :
                    audienceSize.users_upper_bound > 100_000 ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                    'bg-amber-50 text-amber-700 border border-amber-200'
                  }`}>
                    {t('campaignDraft.audience.reachRange', { lower: formatNum(audienceSize.users_lower_bound), upper: formatNum(audienceSize.users_upper_bound), geo: geoLabel })}
                  </span>
                ) : allInterests.some(i => !String(i.id).startsWith('ai_')) ? (
                  <span className="text-[10px] text-gray-400">{t('campaignDraft.audience.connectMetaHint')}</span>
                ) : allInterests.length > 0 ? (
                  <span className="text-[10px] text-gray-400">{t('campaignDraft.audience.addFromCatalogHint')}</span>
                ) : null}
              </div>
              <p className="text-sm text-gray-500 mb-3">
                {t('campaignDraft.audience.subtitle')}
              </p>

              {/* Unmatched interests note */}
              {(draft.audience?.interests_unmatched || []).length > 0 && allInterests.length < 5 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                  <p className="text-xs font-medium text-amber-800 mb-1">{t('campaignDraft.audience.verifyTitle')}</p>
                  <p className="text-xs text-amber-700 mb-2">
                    {t('campaignDraft.audience.verifyText')}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {draft.audience.interests_unmatched.map((name, i) => (
                      <span key={i} className="text-[11px] bg-white border border-amber-200 rounded px-2 py-0.5 text-amber-700">{name}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Age & Gender */}
              <div className="bg-gray-50 rounded-lg p-3 mb-3 border border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2">{t('campaignDraft.audience.demographics')}</p>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="text-[10px] text-gray-500 uppercase tracking-wide">{t('campaignDraft.audience.ageFrom')}</label>
                    <input type="number" min="13" max="65" value={audience.age_min || 18}
                      onChange={e => { const u = { ...audience, age_min: +e.target.value }; setAudience(u); fetchAudienceSize(u); }}
                      className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm mt-0.5" />
                  </div>
                  <div className="flex-1">
                    <label className="text-[10px] text-gray-500 uppercase tracking-wide">{t('campaignDraft.audience.ageTo')}</label>
                    <input type="number" min="13" max="65" value={audience.age_max || 55}
                      onChange={e => { const u = { ...audience, age_max: +e.target.value }; setAudience(u); fetchAudienceSize(u); }}
                      className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm mt-0.5" />
                  </div>
                  <div className="flex-1">
                    <label className="text-[10px] text-gray-500 uppercase tracking-wide">{t('campaignDraft.audience.gender')}</label>
                    <select value={(audience.genders || ['all'])[0]}
                      onChange={e => { const u = { ...audience, genders: [e.target.value] }; setAudience(u); fetchAudienceSize(u); }}
                      className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm mt-0.5 bg-white">
                      <option value="all">{t('campaignDraft.audience.genderAll')}</option>
                      <option value="male">{t('campaignDraft.audience.genderMale')}</option>
                      <option value="female">{t('campaignDraft.audience.genderFemale')}</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Interests with search */}
              <div className="bg-gray-50 rounded-lg p-3 mb-3 border border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2">{t('campaignDraft.audience.interests')}</p>

                {/* Selected interests */}
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {allInterests.map((interest) => {
                    const isUnvalidated = String(interest.id).startsWith('ai_');
                    return (
                    <span key={interest.id} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${
                      isUnvalidated ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      interest.ai_suggested ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-indigo-50 text-indigo-700 border-indigo-200'
                    }`}>
                      {interest.name}
                      {interest.audience_size > 0 && (
                        <span className="text-[9px] opacity-60 ml-0.5">({formatNum(interest.audience_size)})</span>
                      )}
                      {isUnvalidated ? (
                        <span className="text-[9px] opacity-60 ml-0.5">{t('campaignDraft.audience.unvalidated')}</span>
                      ) : interest.ai_suggested ? (
                        <span className="text-[9px] opacity-60 ml-0.5">AI</span>
                      ) : null}
                      <button onClick={() => removeInterest(interest.id)}
                        className="opacity-50 hover:opacity-100 ml-0.5">&times;</button>
                    </span>
                    );
                  })}
                  {allInterests.length === 0 && (
                    <span className="text-xs text-gray-400">{t('campaignDraft.audience.emptyInterests')}</span>
                  )}
                </div>

                {/* Search input */}
                <div className="relative">
                  <input
                    type="text"
                    value={interestQuery}
                    onChange={e => searchInterests(e.target.value)}
                    placeholder={t('campaignDraft.audience.searchPlaceholder')}
                    className="w-full border border-gray-200 rounded px-2.5 py-1.5 text-sm pr-8"
                  />
                  {interestLoading && (
                    <CircleNotch size={14} className="animate-spin absolute right-2.5 top-2 text-gray-400" />
                  )}

                  {/* Search results dropdown */}
                  {interestResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {interestResults.map(interest => {
                        const alreadyAdded = allInterests.some(i => i.id === interest.id);
                        return (
                          <button
                            key={interest.id}
                            onClick={() => !alreadyAdded && addInterest(interest)}
                            disabled={alreadyAdded}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-gray-50 border-b border-gray-50 last:border-0 ${
                              alreadyAdded ? 'opacity-40 cursor-default' : 'cursor-pointer'
                            }`}
                          >
                            <div className="min-w-0">
                              <span className="font-medium">{interest.name}</span>
                              {interest.path?.length > 0 && (
                                <span className="text-[10px] text-gray-400 ml-1.5">{interest.path.join(' > ')}</span>
                              )}
                            </div>
                            <span className="text-[10px] text-gray-400 shrink-0 ml-2">
                              {formatNum(interest.audience_size_lower_bound || interest.audience_size)} - {formatNum(interest.audience_size_upper_bound || interest.audience_size)} {t('campaignDraft.audience.globalAbbr')}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Behaviors */}
              {(audience.behaviors || []).length > 0 && (
                <div className="bg-gray-50 rounded-lg p-3 mb-3 border border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 mb-2">{t('campaignDraft.audience.behaviors')}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {audience.behaviors.map((b, i) => (
                      <span key={i} className="inline-flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full border border-gray-200">
                        {b}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Lookalike suggestion */}
              {audience.lookalike_suggestion && (
                <div className="bg-blue-50 rounded-lg p-3 mb-3 border border-blue-200">
                  <p className="text-xs font-semibold text-blue-700 mb-1">{t('campaignDraft.audience.lookalikeTitle')}</p>
                  <p className="text-xs text-blue-600">{audience.lookalike_suggestion}</p>
                </div>
              )}

              {/* Creative suggestions */}
              {creativeSuggestions.length > 0 && (
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                  <p className="text-xs font-semibold text-amber-700 mb-2">{t('campaignDraft.audience.creativeSuggestionsTitle')}</p>
                  <ul className="space-y-1">
                    {creativeSuggestions.map((s, i) => (
                      <li key={i} className="text-xs text-amber-700 flex items-start gap-1.5">
                        <span className="text-amber-400 mt-0.5">•</span>
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            );
          })()}

          {step === 3 && !isMetaPlatform && (
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">{t('campaignDraft.keywords.title')}</h3>
              <p className="text-sm text-gray-500 mb-3">
                {hasAdGroups ? t('campaignDraft.keywords.subtitleGrouped') : t('campaignDraft.keywords.subtitleDefault')}
              </p>

              {/* Budget mode toggle */}
              <div className={`rounded-lg px-3.5 py-3 mb-4 border ${keywordStrategy === 'budget' ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="min-w-0">
                    <p className={`text-xs font-semibold ${keywordStrategy === 'budget' ? 'text-amber-800' : 'text-gray-700'}`}>{t('campaignDraft.keywords.budgetModeTitle')}</p>
                    <p className="text-[10px] text-gray-500 leading-tight mt-0.5">{t('campaignDraft.keywords.budgetModeDesc')}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <select
                    value={budgetKwCount}
                    onChange={e => setBudgetKwCount(e.target.value)}
                    className="border border-gray-200 rounded px-2 py-1 pr-6 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 min-w-[85px]"
                  >
                    <option value="10">{t('campaignDraft.keywords.phrasesOption', { count: 10 })}</option>
                    <option value="15">{t('campaignDraft.keywords.phrasesOption', { count: 15 })}</option>
                    <option value="20">{t('campaignDraft.keywords.phrasesOption', { count: 20 })}</option>
                    <option value="30">{t('campaignDraft.keywords.phrasesOption', { count: 30 })}</option>
                    <option value="40">{t('campaignDraft.keywords.phrasesOption', { count: 40 })}</option>
                  </select>

                  <button
                    onClick={async () => {
                      // Save original keywords before switching
                      if (keywordStrategy !== 'budget' && !originalKeywords) {
                        setOriginalKeywords([...keywords]);
                      }
                      setRegeneratingKw(true);
                      try {
                        const res = await api.post(`/ads/drafts/${campaign.id}/regenerate-keywords/`, {
                          keyword_strategy: 'budget',
                          keyword_count: parseInt(budgetKwCount),
                        });
                        const newDraft = res.data.ai_draft_data || {};
                        const newRawKw = [];
                        if (newDraft.ad_groups) {
                          newDraft.ad_groups.forEach(ag => {
                            (ag.keywords || []).forEach(kw => newRawKw.push({ ...kw, ad_group_name: ag.name, final_url: ag.final_url }));
                          });
                        } else {
                          (newDraft.keywords || []).forEach(kw => newRawKw.push(kw));
                        }
                        setKeywords(newRawKw.map((kw, i) => ({
                          id: i, text: kw.text || '', match_type: kw.match_type || 'PHRASE',
                          estimated_cpc: kw.estimated_cpc || null, relevance: kw.relevance || 'Wysoka',
                          ad_group_name: kw.ad_group_name || '', final_url: kw.final_url || '',
                        })));
                        setKeywordStrategy('budget');
                      } catch { /* keep current */ }
                      setRegeneratingKw(false);
                    }}
                    disabled={regeneratingKw}
                    className="px-3 py-1 rounded-md text-xs font-medium bg-amber-600 text-white hover:bg-amber-700 transition disabled:opacity-50"
                  >
                    {regeneratingKw ? t('campaignDraft.common.generating') : keywordStrategy === 'budget' ? t('campaignDraft.common.regenerate') : t('campaignDraft.keywords.generateCheaper')}
                  </button>

                  {keywordStrategy === 'budget' && originalKeywords && (
                    <button
                      onClick={() => {
                        setKeywords(originalKeywords);
                        setKeywordStrategy('standard');
                        setOriginalKeywords(null);
                      }}
                      className="px-3 py-1 rounded-md text-xs font-medium bg-white text-gray-600 border border-gray-300 hover:bg-gray-50 transition"
                    >
                      {t('campaignDraft.keywords.restoreOriginal')}
                    </button>
                  )}
                </div>
              </div>

              {hasAdGroups ? (
                /* Grouped keywords by ad group */
                <div className="space-y-4 mb-4">
                  {draft.ad_groups.map((ag, agIdx) => {
                    const agKeywords = keywords.filter(kw => kw.ad_group_name === ag.name);
                    return (
                      <div key={agIdx} className="border border-gray-200 rounded-lg overflow-x-auto">
                        <div className="bg-indigo-50 px-3 py-2 border-b border-indigo-100 flex items-center justify-between">
                          <div>
                            <span className="text-xs font-semibold text-indigo-700">{ag.name}</span>
                            <span className="text-[10px] text-indigo-400 ml-2">{t('campaignDraft.keywords.wordsShort', { count: agKeywords.length })}</span>
                            {(() => {
                              const cpcs = agKeywords.filter(k => k.estimated_cpc).map(k => parseFloat(k.estimated_cpc));
                              if (cpcs.length === 0) return null;
                              const avg = (cpcs.reduce((a, b) => a + b, 0) / cpcs.length).toFixed(2);
                              return <span className="text-[10px] text-green-600 ml-2">{t('campaignDraft.keywords.avgCpcLabel', { cpc: avg, cur: curSym })}</span>;
                            })()}
                          </div>
                          {ag.final_url && (
                            <span className="text-[10px] text-gray-400 truncate max-w-[200px]">{ag.final_url.split('?')[0]}</span>
                          )}
                        </div>
                        <table className="w-full" style={{ tableLayout: 'fixed' }}>
                          <colgroup>
                            <col />
                            <col style={{ width: 105 }} />
                            <col style={{ width: 70 }} />
                            <col style={{ width: 28 }} />
                          </colgroup>
                          <thead>
                            <tr className="border-b border-gray-100 bg-gray-50/50">
                              <th className="text-left text-[10px] font-medium text-gray-400 py-1 px-3">{t('campaignDraft.keywords.colKeyword')}</th>
                              <th className="text-left text-[10px] font-medium text-gray-400 py-1 px-2">{t('campaignDraft.keywords.colMatchShort')}</th>
                              <th className="text-right text-[10px] font-medium text-gray-400 py-1 px-2">{t('campaignDraft.keywords.colCpc')}</th>
                              <th className="py-1 px-2"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {agKeywords.map(kw => (
                              <tr key={kw.id} className="border-b border-gray-100 last:border-0">
                                <td className="py-1.5 px-3 text-sm text-gray-900">{kw.text}</td>
                                <td className="py-1.5 px-2">
                                  <select
                                    value={kw.match_type}
                                    onChange={e => updateKeywordMatchType(kw.id, e.target.value)}
                                    className="text-[11px] border border-gray-200 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 w-full"
                                  >
                                    <option value="EXACT">{t('campaignDraft.matchType.exact')}</option>
                                    <option value="PHRASE">{t('campaignDraft.matchType.phrase')}</option>
                                    <option value="BROAD">{t('campaignDraft.matchType.broad')}</option>
                                  </select>
                                </td>
                                <td className="py-1.5 px-2 text-right text-xs text-gray-500">
                                  {kw.estimated_cpc ? `${kw.estimated_cpc} ${curSym}` : '-'}
                                </td>
                                <td className="py-1.5 px-2 w-8">
                                  <button onClick={() => removeKeyword(kw.id)} className="text-gray-300 hover:text-red-500 transition">
                                    <X className="w-3 h-3" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              ) : (
              /* Flat keywords table */
              <>
              <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left text-[11px] font-medium text-gray-500 py-2 px-3">{t('campaignDraft.keywords.colKeyword')}</th>
                      <th className="text-left text-[11px] font-medium text-gray-500 py-2 px-2">{t('campaignDraft.keywords.colMatch')}</th>
                      <th className="text-right text-[11px] font-medium text-gray-500 py-2 px-2">{t('campaignDraft.keywords.colVolume')}</th>
                      <th className="text-center text-[11px] font-medium text-gray-500 py-2 px-2">{t('campaignDraft.keywords.colCompetition')}</th>
                      <th className="text-right text-[11px] font-medium text-gray-500 py-2 px-2">{t('campaignDraft.keywords.colCpc')}</th>
                      <th className="py-2 px-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {keywords.map((kw) => (
                      <tr key={kw.id} className="border-b border-gray-100 last:border-0">
                        <td className="py-2 px-3 text-sm text-gray-900">{kw.text}</td>
                        <td className="py-2 px-2">
                          <select
                            value={kw.match_type}
                            onChange={e => updateKeywordMatchType(kw.id, e.target.value)}
                            className="text-xs border border-gray-200 rounded px-1.5 py-1 w-[110px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          >
                            <option value="EXACT">{t('campaignDraft.matchType.exact')}</option>
                            <option value="PHRASE">{t('campaignDraft.matchType.phrase')}</option>
                            <option value="BROAD">{t('campaignDraft.matchType.broad')}</option>
                          </select>
                        </td>
                        <td className="py-2 px-2 text-right text-xs text-gray-600">
                          {kw.avg_monthly_searches ? kw.avg_monthly_searches.toLocaleString('pl-PL') : '-'}
                        </td>
                        <td className="py-2 px-2 text-center">
                          {kw.competition ? (
                            <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${kw.competition === 'HIGH' ? 'bg-red-50 text-red-600' : kw.competition === 'MEDIUM' ? 'bg-yellow-50 text-yellow-700' : 'bg-green-50 text-green-600'}`}>
                              {kw.competition === 'HIGH' ? t('campaignDraft.competition.high') : kw.competition === 'MEDIUM' ? t('campaignDraft.competition.medium') : t('campaignDraft.competition.low')}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="py-2 px-2 text-right text-xs text-gray-600">
                          {kw.high_cpc ? `${kw.low_cpc}-${kw.high_cpc} ${curSym}` : kw.estimated_cpc ? `${kw.estimated_cpc} ${curSym}` : '-'}
                        </td>
                        <td className="py-2 px-2">
                          <button
                            onClick={() => removeKeyword(kw.id)}
                            className="text-gray-300 hover:text-red-500 transition"
                          >
                            <Trash className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-end mt-2 mb-2">
                <span className="text-xs text-gray-400">{t('campaignDraft.keywords.keywordsCount', { count: keywords.length })}</span>
              </div>

              {/* Wykluczenia: bez nich kampania płaci za zapytania z innej intencji. */}
              <div className="mt-6 pt-5 border-t border-gray-100">
                <h4 className="text-sm font-semibold text-gray-900">{t('campaignDraft.keywords.negativesTitle')}</h4>
                <p className="text-xs text-gray-500 mt-0.5 mb-3">{t('campaignDraft.keywords.negativesSubtitle')}</p>

                {negativeKeywords.length === 0 ? (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-3 py-2">
                    {t('campaignDraft.keywords.negativesEmpty')}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {negativeKeywords.map((n) => (
                      <span
                        key={n.id}
                        title={n.reason || ''}
                        className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded px-2 py-1 text-xs text-gray-700"
                      >
                        {n.text}
                        <span className="text-[10px] text-gray-400">{n.match_type === 'PHRASE' ? t('campaignDraft.matchType.phrase') : t('campaignDraft.matchType.broad')}</span>
                        <button onClick={() => removeNegative(n.id)} className="text-gray-300 hover:text-red-500 transition">
                          <Trash className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-2 mt-3">
                  <input
                    value={newNegative}
                    onChange={e => setNewNegative(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNegative(); } }}
                    placeholder={t('campaignDraft.keywords.negativesAddPlaceholder')}
                    className="flex-1 max-w-xs border border-gray-200 rounded px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  <button
                    onClick={addNegative}
                    className="text-xs bg-indigo-600 text-white rounded px-3 py-1.5 hover:bg-indigo-700 transition"
                  >
                    {t('campaignDraft.keywords.negativesAdd')}
                  </button>
                  <span className="text-xs text-gray-400 ml-auto">{t('campaignDraft.keywords.negativesCount', { count: negativeKeywords.length })}</span>
                </div>

                <p className="text-[11px] text-gray-400 mt-2 leading-snug">{t('campaignDraft.keywords.negativesHint')}</p>
              </div>
              </>
              )}

            </div>
          )}

          {/* ===== STEP 4: Ad copy (platform-aware) ===== */}
          {step === 4 && isMetaPlatform && (
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">{t('campaignDraft.metaAd.title')}</h3>
              <p className="text-sm text-gray-500 mb-4">{t('campaignDraft.metaAd.subtitle')}</p>

              {/* Primary text */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{t('campaignDraft.metaAd.primaryText')}</label>
                  <CharCounter current={primaryText.length} max={125} />
                </div>
                <textarea
                  value={primaryText}
                  onChange={e => setPrimaryText(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400"
                  placeholder={t('campaignDraft.metaAd.primaryTextPlaceholder')}
                />
              </div>

              {/* Headlines */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{t('campaignDraft.metaAd.headlines')}</label>
                  <AiBadge />
                </div>
                <div className="space-y-1.5">
                  {headlines.map((h, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        value={h}
                        onChange={e => setHeadlines(prev => prev.map((x, j) => j === i ? e.target.value : x))}
                        className="flex-1 border border-gray-200 rounded px-2.5 py-1.5 text-sm"
                        maxLength={40}
                      />
                      <CharCounter current={h.length} max={40} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Descriptions */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{t('campaignDraft.metaAd.descriptions')}</label>
                </div>
                <div className="space-y-1.5">
                  {descriptions.map((d, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        value={d}
                        onChange={e => setDescriptions(prev => prev.map((x, j) => j === i ? e.target.value : x))}
                        className="flex-1 border border-gray-200 rounded px-2.5 py-1.5 text-sm"
                        maxLength={125}
                      />
                      <CharCounter current={d.length} max={125} />
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Button */}
              <div className="mb-4">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide block mb-1">{t('campaignDraft.metaAd.ctaButton')}</label>
                <select
                  value={ctaButton}
                  onChange={e => setCtaButton(e.target.value)}
                  className="border border-gray-200 rounded px-2.5 py-1.5 text-sm bg-white w-full"
                >
                  <option value="LEARN_MORE">{t('campaignDraft.cta.learnMore')}</option>
                  <option value="SHOP_NOW">{t('campaignDraft.cta.shopNow')}</option>
                  <option value="SIGN_UP">{t('campaignDraft.cta.signUp')}</option>
                  <option value="CONTACT_US">{t('campaignDraft.cta.contactUs')}</option>
                  <option value="GET_OFFER">{t('campaignDraft.cta.getOffer')}</option>
                  <option value="BOOK_NOW">{t('campaignDraft.cta.bookNow')}</option>
                </select>
              </div>

              {/* Final URL */}
              <div>
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide block mb-1">{t('campaignDraft.metaAd.finalUrl')}</label>
                <input
                  value={finalUrl}
                  onChange={e => setFinalUrl(e.target.value)}
                  className="w-full border border-gray-200 rounded px-2.5 py-1.5 text-sm"
                  placeholder="https://..."
                />
              </div>
            </div>
          )}

          {step === 4 && !isMetaPlatform && (
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">{t('campaignDraft.googleAd.title')}</h3>
              <p className="text-sm text-gray-500 mb-4">
                {hasAdGroups ? t('campaignDraft.googleAd.subtitleGrouped') : t('campaignDraft.googleAd.subtitleDefault')}
              </p>

              {hasAdGroups ? (
                <>
                  {/* Ad group selector */}
                  {adGroupsState.length <= 3 ? (
                    <div className="flex gap-1.5 mb-4">
                      {adGroupsState.map((ag, i) => (
                        <button
                          key={ag.id}
                          onClick={() => setActiveAdGroup(i)}
                          className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition border ${
                            activeAdGroup === i
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
                          }`}
                        >
                          <span className="block truncate">{ag.name || t('campaignDraft.googleAd.groupFallback', { num: i + 1 })}</span>
                          <span className={`block text-[10px] mt-0.5 ${activeAdGroup === i ? 'text-indigo-200' : 'text-gray-400'}`}>
                            {t('campaignDraft.googleAd.groupCounts', { h: ag.headlines?.length || 0, d: ag.descriptions?.length || 0 })}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="mb-4">
                      <Select
                        value={String(activeAdGroup)}
                        onChange={v => setActiveAdGroup(Number(v))}
                        data={adGroupsState.map((ag, i) => ({
                          value: String(i),
                          label: `${ag.name || t('campaignDraft.googleAd.groupFallback', { num: i + 1 })}`,
                          description: t('campaignDraft.googleAd.groupCountsLong', { h: ag.headlines?.length || 0, d: ag.descriptions?.length || 0 }),
                        }))}
                        size="sm"
                        allowDeselect={false}
                        styles={{ input: { fontWeight: 500 } }}
                      />
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] text-gray-400">{t('campaignDraft.googleAd.groupPosition', { current: activeAdGroup + 1, total: adGroupsState.length })}</span>
                        <div className="flex gap-1">
                          <button
                            onClick={() => setActiveAdGroup(Math.max(0, activeAdGroup - 1))}
                            disabled={activeAdGroup === 0}
                            className="px-2 py-1 rounded text-xs text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                          >{t('campaignDraft.googleAd.prevGroup')}</button>
                          <button
                            onClick={() => setActiveAdGroup(Math.min(adGroupsState.length - 1, activeAdGroup + 1))}
                            disabled={activeAdGroup === adGroupsState.length - 1}
                            className="px-2 py-1 rounded text-xs text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                          >{t('campaignDraft.googleAd.nextGroup')}</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Active ad group content */}
                  {adGroupsState[activeAdGroup] && (() => {
                    const ag = adGroupsState[activeAdGroup];
                    const updateAg = (field, value) => {
                      setAdGroupsState(prev => prev.map((a, i) => i === activeAdGroup ? { ...a, [field]: value } : a));
                    };
                    return (
                      <div key={ag.id}>
                        {/* URL */}
                        <div className="mb-4">
                          <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('campaignDraft.googleAd.targetUrl')}</label>
                          <input
                            type="url"
                            value={ag.final_url}
                            onChange={e => updateAg('final_url', e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          />
                        </div>

                        {/* Headlines */}
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-xs font-medium text-gray-600">{t('campaignDraft.googleAd.headlinesShort')}</label>
                            <AiBadge />
                          </div>
                          <div className="space-y-1.5">
                            {ag.headlines.map((h, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <input
                                  type="text" value={h} maxLength={30}
                                  onChange={e => { const nh = [...ag.headlines]; nh[i] = e.target.value; updateAg('headlines', nh); }}
                                  className="flex-1 border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                                <CharCounter current={h.length} max={30} />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Descriptions */}
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-xs font-medium text-gray-600">{t('campaignDraft.googleAd.descriptionsShort')}</label>
                            <AiBadge />
                          </div>
                          <div className="space-y-1.5">
                            {ag.descriptions.map((d, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <input
                                  type="text" value={d} maxLength={90}
                                  onChange={e => { const nd = [...ag.descriptions]; nd[i] = e.target.value; updateAg('descriptions', nd); }}
                                  className="flex-1 border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                                <CharCounter current={d.length} max={90} />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Preview */}
                        <div className="mb-2">
                          <p className="text-xs font-medium text-gray-500 mb-2">{t('campaignDraft.googleAd.adPreview')}</p>
                          <div className="border border-gray-200 rounded-lg p-3 bg-white">
                            <p className="text-xs text-gray-500 mb-1">{t('campaignDraft.googleAd.ad')}</p>
                            <p className="text-indigo-700 text-sm font-medium leading-snug">
                              {ag.headlines.filter(h => h.trim()).slice(0, 3).join(' | ') || t('campaignDraft.googleAd.headlineFallback')}
                            </p>
                            <p className="text-green-700 text-[11px] mt-0.5 truncate">{ag.final_url || 'https://...'}</p>
                            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                              {ag.descriptions.filter(d => d.trim()).slice(0, 2).join(' ') || t('campaignDraft.googleAd.descriptionFallback')}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <>
                  {/* Single campaign: original flat layout */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-gray-600">{t('campaignDraft.googleAd.headlinesLong')}</label>
                      <AiBadge />
                    </div>
                    <div className="space-y-2">
                      {headlines.map((h, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-[10px] text-gray-400 w-4 text-right">{i + 1}.</span>
                          <input
                            type="text" value={h} maxLength={30}
                            onChange={e => updateHeadline(i, e.target.value)}
                            className="flex-1 border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                          <CharCounter current={h.length} max={30} />
                          {headlines.length > 1 && (
                            <button onClick={() => removeHeadline(i)} className="text-gray-300 hover:text-red-500 transition"><X className="w-3.5 h-3.5" /></button>
                          )}
                        </div>
                      ))}
                    </div>
                    {headlines.length < 15 && (
                      <button onClick={addHeadline} className="mt-2 text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1 font-medium">
                        <Plus className="w-3 h-3" /> {t('campaignDraft.googleAd.addHeadline')}
                      </button>
                    )}
                  </div>

                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-gray-600">{t('campaignDraft.googleAd.descriptionsLong')}</label>
                      <AiBadge />
                    </div>
                    <div className="space-y-2">
                      {descriptions.map((d, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-[10px] text-gray-400 w-4 text-right">{i + 1}.</span>
                          <input
                            type="text" value={d} maxLength={90}
                            onChange={e => updateDescription(i, e.target.value)}
                            className="flex-1 border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                          <CharCounter current={d.length} max={90} />
                          {descriptions.length > 1 && (
                            <button onClick={() => removeDescription(i)} className="text-gray-300 hover:text-red-500 transition"><X className="w-3.5 h-3.5" /></button>
                          )}
                        </div>
                      ))}
                    </div>
                    {descriptions.length < 4 && (
                      <button onClick={addDescription} className="mt-2 text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1 font-medium">
                        <Plus className="w-3 h-3" /> {t('campaignDraft.googleAd.addDescription')}
                      </button>
                    )}
                  </div>

                  <div className="mb-6">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('campaignDraft.googleAd.targetUrl')}</label>
                    <input
                      type="url" value={finalUrl} onChange={e => setFinalUrl(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div className="mb-4">
                    <p className="text-xs font-medium text-gray-500 mb-2">{isMetaPlatform ? t('campaignDraft.googleAd.previewMeta') : t('campaignDraft.googleAd.previewGoogle')}</p>
                    <div className="border border-gray-200 rounded-lg p-4 bg-white">
                      <p className="text-xs text-gray-500 mb-1">{t('campaignDraft.googleAd.ad')}</p>
                      <p className="text-indigo-700 text-base font-medium leading-snug">
                        {headlines.filter(h => h.trim()).slice(0, 3).join(' | ') || t('campaignDraft.googleAd.headlineAdFallback')}
                      </p>
                      <p className="text-green-700 text-xs mt-0.5">{finalUrl || 'https://twoja-strona.pl'}</p>
                      <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                        {descriptions.filter(d => d.trim()).slice(0, 2).join(' ') || t('campaignDraft.googleAd.descriptionAdFallback')}
                      </p>
                    </div>
                  </div>
                </>
              )}

            </div>
          )}

          {/* ===== STEP 5 (Meta): Kreacja wizualna ===== */}
          {step === stepCreative && isMetaPlatform && (() => {
            const handleGenerate = async () => {
              setAiGenerating(true);
              setAiGenerateError('');
              try {
                // Kwadrat (1:1) to baza dla feedów Meta; wersja 9:16 dla Stories/Reels powstaje automatycznie.
                const res = await api.post(`/ads/drafts/${campaign.id}/images/generate/`, { aspect_ratio: 'square' });
                if (res.data.images?.length > 0) {
                  setCreativeImages(prev => [...res.data.images, ...prev]);
                }
              } catch (err) {
                setAiGenerateError(err.response?.data?.error || t('campaignDraft.messages.imageGenError'));
              }
              setAiGenerating(false);
            };

            const handleExtract = async () => {
              setCreativeLoading(true);
              try {
                const res = await api.post(`/ads/drafts/${campaign.id}/images/extract/`, { url: campaign.source_url });
                setCreativeImages(prev => [...prev, ...(res.data.images || [])]);
              } catch {} finally { setCreativeLoading(false); }
            };

            const handleUpload = async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setCreativeUploading(true);
              try {
                const formData = new FormData();
                formData.append('image', file);
                formData.append('label', file.name);
                const res = await api.post(`/ads/drafts/${campaign.id}/images/upload/`, formData, {
                  headers: { 'Content-Type': 'multipart/form-data' },
                });
                if (res.data.image) setCreativeImages(prev => [res.data.image, ...prev]);
              } catch {} finally { setCreativeUploading(false); e.target.value = ''; }
            };

            const toggleSelect = async (img) => {
              const updated = !img.is_selected;
              try {
                await api.patch(`/ads/drafts/${campaign.id}/images/${img.id}/`, { is_selected: updated });
                setCreativeImages(prev => prev.map(i => i.id === img.id ? { ...i, is_selected: updated } : i));
              } catch {}
            };

            const deleteImage = async (imgId) => {
              try {
                await api.delete(`/ads/drafts/${campaign.id}/images/${imgId}/`);
                setCreativeImages(prev => prev.filter(i => i.id !== imgId));
                if (previewImage?.id === imgId) setPreviewImage(null);
              } catch {}
            };

            const handleVideoUpload = async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setVideoUploading(true);
              try {
                const formData = new FormData();
                formData.append('video', file);
                formData.append('label', file.name);
                const res = await api.post(`/ads/drafts/${campaign.id}/videos/upload/`, formData, {
                  headers: { 'Content-Type': 'multipart/form-data' },
                });
                if (res.data.video) setCreativeVideos(prev => [res.data.video, ...prev]);
              } catch {} finally { setVideoUploading(false); e.target.value = ''; }
            };

            const toggleVideoSelect = async (vid) => {
              const updated = !vid.is_selected;
              try {
                await api.patch(`/ads/drafts/${campaign.id}/videos/${vid.id}/`, { is_selected: updated });
                setCreativeVideos(prev => prev.map(v => v.id === vid.id ? { ...v, is_selected: updated } : v));
              } catch {}
            };

            const deleteVideo = async (vidId) => {
              try {
                await api.delete(`/ads/drafts/${campaign.id}/videos/${vidId}/`);
                setCreativeVideos(prev => prev.filter(v => v.id !== vidId));
              } catch {}
            };

            const selectedImage = creativeImages.find(i => i.is_selected);
            const apiBase = (process.env.REACT_APP_API_URL || '/api').replace('/api', '');
            const imgUrl = (path) => path?.startsWith('http') ? path : `${apiBase}${path}`;

            return (
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">{t('campaignDraft.creative.title')}</h3>
              <p className="text-sm text-gray-500 mb-5">
                {t('campaignDraft.creative.subtitle')}
              </p>

              {/* Facebook Page selector */}
              <div className="mb-5">
                <label className="text-xs font-medium text-gray-600 mb-1.5 block">{t('campaignDraft.creative.fbPage')}</label>
                {fbPagesLoading ? (
                  <div className="flex items-center gap-2 py-2">
                    <CircleNotch size={14} className="animate-spin text-gray-400" />
                    <span className="text-xs text-gray-400">{t('campaignDraft.creative.loadingPages')}</span>
                  </div>
                ) : fbPages.length > 0 ? (
                  <div className="space-y-1.5">
                    {fbPages.map(page => (
                      <button
                        key={page.id}
                        onClick={() => {
                          setSelectedPageId(page.id);
                          api.post('/ads/meta/pages/', { page_id: page.id, page_name: page.name }).catch(() => {});
                        }}
                        className={`w-full text-left flex items-center gap-2.5 border rounded-lg px-3 py-2 transition ${
                          selectedPageId === page.id ? 'border-indigo-500 bg-indigo-50/50 ring-1 ring-indigo-200' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {page.picture ? (
                          <img src={page.picture} alt="" className="w-7 h-7 rounded-full" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-500">{page.name?.charAt(0)}</div>
                        )}
                        <div>
                          <p className={`text-sm font-medium ${selectedPageId === page.id ? 'text-indigo-700' : 'text-gray-800'}`}>{page.name}</p>
                          <p className="text-[10px] text-gray-400">ID: {page.id}</p>
                        </div>
                        {selectedPageId === page.id && <Check size={14} className="ml-auto text-indigo-500" />}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs font-medium text-amber-800 mb-1">{t('campaignDraft.creative.noPageTitle')}</p>
                    <p className="text-xs text-amber-700">
                      {t('campaignDraft.creative.noPageText')}
                    </p>
                  </div>
                )}
              </div>

              {/* Upload zone */}
              <div className="mb-5">
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-lg p-5 hover:border-indigo-300 hover:bg-indigo-50/30 transition cursor-pointer">
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleUpload} disabled={creativeUploading} />
                  {creativeUploading ? (
                    <><CircleNotch className="w-5 h-5 animate-spin text-indigo-500" /><span className="text-sm text-gray-500">{t('campaignDraft.creative.uploading')}</span></>
                  ) : (
                    <><Upload className="w-5 h-5 text-gray-400" /><span className="text-sm text-gray-500">{t('campaignDraft.creative.uploadImage')}</span></>
                  )}
                </label>
                {creativeImages.length === 0 && !creativeLoading && creativesFetched && (
                  <button onClick={handleExtract} className="mt-3 w-full text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center justify-center gap-1.5 py-2 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition">
                    <ImageIcon className="w-3.5 h-3.5" />
                    {t('campaignDraft.creative.extractImages', { host: campaign.source_url ? new URL(campaign.source_url).hostname : '' })}
                  </button>
                )}
              </div>

              {/* AI Image Generator */}
              <button
                onClick={handleGenerate}
                disabled={aiGenerating}
                className="mb-3 w-full text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center justify-center gap-1.5 py-2 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition disabled:opacity-50"
              >
                {aiGenerating ? (
                  <><CircleNotch className="w-3.5 h-3.5 animate-spin" /> {t('campaignDraft.creative.generatingAi')}</>
                ) : (
                  <><Sparkle className="w-3.5 h-3.5" weight="duotone" /> {t('campaignDraft.creative.generateAi')}</>
                )}
              </button>
              {aiGenerateError && (
                <p className="text-xs text-red-500 mb-3">{aiGenerateError}</p>
              )}

              {/* Video upload (Stories/Reels/feed) */}
              <div className="mb-5">
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-lg p-3 hover:border-indigo-300 hover:bg-indigo-50/30 transition cursor-pointer">
                  <input type="file" accept="video/mp4,video/quicktime" className="hidden" onChange={handleVideoUpload} disabled={videoUploading} />
                  {videoUploading ? (
                    <><CircleNotch className="w-4 h-4 animate-spin text-indigo-500" /><span className="text-xs text-gray-500">{t('campaignDraft.creative.uploadingVideo')}</span></>
                  ) : (
                    <><Upload className="w-4 h-4 text-gray-400" /><span className="text-xs text-gray-500">{t('campaignDraft.creative.uploadVideo')}</span></>
                  )}
                </label>
                {creativeVideos.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {creativeVideos.map(vid => (
                      <div key={vid.id} className={`flex items-center gap-2.5 border rounded-lg px-3 py-2 ${vid.is_selected ? 'border-indigo-300 bg-indigo-50/40' : 'border-gray-200'}`}>
                        <button
                          onClick={() => toggleVideoSelect(vid)}
                          className={`w-5 h-5 rounded-full flex items-center justify-center transition shrink-0 ${vid.is_selected ? 'bg-indigo-500 text-white' : 'bg-white text-gray-400 border border-gray-300'}`}
                        >
                          {vid.is_selected && <Check className="w-3 h-3" />}
                        </button>
                        {vid.thumbnail ? (
                          <img src={imgUrl(vid.thumbnail)} alt="" className="w-8 h-8 rounded object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center text-[10px] text-gray-400">▶</div>
                        )}
                        <p className="text-xs text-gray-700 truncate flex-1">{vid.label}</p>
                        <button onClick={() => deleteVideo(vid.id)} className="text-gray-400 hover:text-red-500 text-xs shrink-0">{t('campaignDraft.common.delete')}</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Loading */}
              {creativeLoading && (
                <div className="flex items-center justify-center gap-2 py-8">
                  <CircleNotch className="w-5 h-5 animate-spin text-indigo-500" />
                  <span className="text-sm text-gray-500">{t('campaignDraft.creative.loadingImages')}</span>
                </div>
              )}

              {/* Image grid */}
              {creativeImages.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-gray-600">{creativeImages.length} {creativeImages.length === 1 ? t('campaignDraft.creative.imageOne') : creativeImages.length < 5 ? t('campaignDraft.creative.imageFew') : t('campaignDraft.creative.imageMany')}</p>
                    <p className="text-xs text-gray-400">{t('campaignDraft.creative.selectedCount', { count: creativeImages.filter(i => i.is_selected).length })}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-5">
                    {creativeImages.map(img => (
                      <div key={img.id} className={`relative group rounded-lg overflow-hidden border-2 transition cursor-pointer ${img.is_selected ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-gray-200 hover:border-gray-300'}`}>
                        <img
                          src={imgUrl(img.crop_square || img.original)}
                          alt={img.label}
                          className="w-full aspect-square object-cover"
                          onClick={() => setPreviewImage(img)}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition" />
                        {/* Select toggle */}
                        <button
                          onClick={() => toggleSelect(img)}
                          className={`absolute top-1.5 left-1.5 w-5 h-5 rounded-full flex items-center justify-center transition ${img.is_selected ? 'bg-indigo-500 text-white' : 'bg-white/80 text-gray-400 border border-gray-300'}`}
                        >
                          {img.is_selected && <Check className="w-3 h-3" />}
                        </button>
                        {/* Delete */}
                        <button
                          onClick={() => deleteImage(img.id)}
                          className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-white/80 text-gray-400 hover:text-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                        >
                          <Trash className="w-3 h-3" />
                        </button>
                        {/* Source badge */}
                        {img.source === 'extracted' && (
                          <span className="absolute bottom-1 left-1 text-[9px] bg-black/50 text-white px-1.5 py-0.5 rounded">{t('campaignDraft.creative.fromSite')}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Crop preview (when image clicked) */}
              {previewImage && (
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-gray-600">{t('campaignDraft.creative.cropPreview')}</p>
                    <button onClick={() => setPreviewImage(null)} className="text-xs text-gray-400 hover:text-gray-600">{t('campaignDraft.common.close')}</button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] text-gray-400 mb-1">{t('campaignDraft.creative.cropSquare')}</p>
                      <img src={imgUrl(previewImage.crop_square || previewImage.original)} alt="Square crop" className="w-full rounded-lg border border-gray-200" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 mb-1">{t('campaignDraft.creative.cropLandscape')}</p>
                      <img src={imgUrl(previewImage.crop_landscape || previewImage.original)} alt="Landscape crop" className="w-full rounded-lg border border-gray-200" />
                    </div>
                  </div>
                </div>
              )}

              {/* Ad preview with selected image */}
              {selectedImage && (
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">{t('campaignDraft.creative.fbPreview')}</p>
                  <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-xs font-bold text-indigo-600">{campaignName.charAt(0).toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-900">{campaignName}</p>
                        <p className="text-[10px] text-gray-400">{t('campaignDraft.creative.sponsored')}</p>
                      </div>
                    </div>
                    <p className="px-3 py-2 text-xs text-gray-700 leading-relaxed">{primaryText || descriptions[0] || t('campaignDraft.creative.adTextFallback')}</p>
                    <img src={imgUrl(selectedImage.crop_landscape || selectedImage.original)} alt="Ad preview" className="w-full" />
                    <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] text-gray-400 uppercase truncate">{(() => { try { return finalUrl ? new URL(finalUrl).hostname : 'twoja-strona.pl'; } catch { return finalUrl; } })()}</p>
                        <p className="text-xs font-semibold text-gray-900 truncate">{headlines[0] || t('campaignDraft.creative.adHeadlineFallback')}</p>
                      </div>
                      <span className="text-[10px] font-medium text-gray-500 border border-gray-300 rounded px-2 py-1 shrink-0 ml-2">
                        {ctaButton === 'SHOP_NOW' ? t('campaignDraft.ctaPreview.shopNow') : ctaButton === 'SIGN_UP' ? t('campaignDraft.ctaPreview.signUp') : ctaButton === 'CONTACT_US' ? t('campaignDraft.ctaPreview.contactUs') : ctaButton === 'GET_OFFER' ? t('campaignDraft.ctaPreview.getOffer') : ctaButton === 'BOOK_NOW' ? t('campaignDraft.ctaPreview.bookNow') : t('campaignDraft.ctaPreview.default')}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            );
          })()}

          {/* ===== STEP Form: Formularz lead gen (Meta leads only) ===== */}
          {step === stepLeadForm && isLeadGen && (() => {
            const addOption = (qi) => updateQuestion(qi, { options: [...(lfQuestions[qi].options || []), ''] });
            const updateOption = (qi, oi, val) =>
              updateQuestion(qi, { options: (lfQuestions[qi].options || []).map((o, idx) => idx === oi ? val : o) });
            const removeOption = (qi, oi) =>
              updateQuestion(qi, { options: (lfQuestions[qi].options || []).filter((_, idx) => idx !== oi) });
            return (
            <div className="p-6 space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{t('campaignDraft.leadForm.title')}</h3>
                  <p className="text-xs text-gray-500 mt-1 max-w-lg">
                    {t('campaignDraft.leadForm.subtitle')}
                  </p>
                </div>
                <button onClick={regenerateLeadForm} disabled={leadFormLoading}
                  className="text-xs text-[#1D4ED8] border border-[#1D4ED8]/30 rounded-lg px-3 py-1.5 hover:bg-[#1D4ED8]/5 transition disabled:opacity-50 shrink-0 flex items-center gap-1">
                  <Sparkle className="w-3.5 h-3.5" /> {leadFormLoading ? t('campaignDraft.common.generating') : t('campaignDraft.common.regenerate')}
                </button>
              </div>

              {leadFormLoading && !leadForm ? (
                <div className="text-sm text-gray-400 py-10 text-center flex items-center justify-center gap-2">
                  <CircleNotch className="w-4 h-4 animate-spin" /> {t('campaignDraft.leadForm.preparing')}
                </div>
              ) : !leadForm ? (
                <div className="text-sm text-gray-400 py-10 text-center">{t('campaignDraft.leadForm.loadError')}</div>
              ) : (
              <>
                {/* Form name */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('campaignDraft.leadForm.nameLabel')}</label>
                  <input type="text" value={lf.name || t('campaignDraft.leadForm.nameFallback', { name: campaignName })}
                    onChange={e => patchLeadForm({ name: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-[#1D4ED8] focus:outline-none" />
                </div>

                {/* Standard prefilled fields */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">{t('campaignDraft.leadForm.contactFields')}</label>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(LEAD_FIELD_LABELS).map(key => {
                      const on = lfFields.includes(key);
                      return (
                        <button key={key} type="button" onClick={() => toggleLeadField(key)}
                          className={`text-xs rounded-full px-3 py-1.5 border transition flex items-center gap-1 ${on ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                          {on && <Check className="w-3 h-3" />}{LEAD_FIELD_LABELS[key]}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Qualifying questions */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-medium text-gray-700">{t('campaignDraft.leadForm.qualifyingQuestions', { count: lfQuestions.length })}</label>
                    {lfQuestions.length < 3 && (
                      <button type="button" onClick={addQuestion} className="text-xs text-[#1D4ED8] hover:underline flex items-center gap-1">
                        <Plus className="w-3 h-3" /> {t('campaignDraft.leadForm.addQuestion')}
                      </button>
                    )}
                  </div>
                  <div className="space-y-3">
                    {lfQuestions.length === 0 && (
                      <div className="text-xs text-gray-400">{t('campaignDraft.leadForm.noQuestions')}</div>
                    )}
                    {lfQuestions.map((q, qi) => (
                      <div key={qi} className="border border-gray-200 rounded-lg p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <input type="text" value={q.label || ''} placeholder={t('campaignDraft.leadForm.questionPlaceholder')}
                            onChange={e => updateQuestion(qi, { label: e.target.value })}
                            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-[#1D4ED8] focus:outline-none" />
                          <button type="button" onClick={() => removeQuestion(qi)} className="text-gray-400 hover:text-red-500 p-1">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="pl-1">
                          <div className="text-[11px] text-gray-400 mb-1">
                            {(q.options || []).length > 0 ? t('campaignDraft.leadForm.closedQuestion') : t('campaignDraft.leadForm.openQuestion')}
                          </div>
                          <div className="space-y-1">
                            {(q.options || []).map((opt, oi) => (
                              <div key={oi} className="flex items-center gap-2">
                                <input type="text" value={opt} placeholder={t('campaignDraft.leadForm.optionPlaceholder', { num: oi + 1 })}
                                  onChange={e => updateOption(qi, oi, e.target.value)}
                                  className="flex-1 border border-gray-200 rounded-md px-2 py-1 text-xs focus:border-[#1D4ED8] focus:outline-none" />
                                <button type="button" onClick={() => removeOption(qi, oi)} className="text-gray-300 hover:text-red-500">
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                          {(q.options || []).length < 5 && (
                            <button type="button" onClick={() => addOption(qi)} className="text-[11px] text-[#1D4ED8] hover:underline mt-1">
                              {t('campaignDraft.leadForm.addOption')}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Thank-you screen + privacy */}
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('campaignDraft.leadForm.thankYouHeadline')}</label>
                    <input type="text" value={lf.thank_you_headline || ''}
                      onChange={e => patchLeadForm({ thank_you_headline: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-[#1D4ED8] focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('campaignDraft.leadForm.thankYouText')}</label>
                    <textarea value={lf.thank_you_description || ''} rows={2}
                      onChange={e => patchLeadForm({ thank_you_description: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-[#1D4ED8] focus:outline-none resize-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('campaignDraft.leadForm.privacyLink')}</label>
                    <input type="url" value={lf.privacy_policy_url || ''} placeholder="https://twojafirma.pl/polityka-prywatnosci"
                      onChange={e => patchLeadForm({ privacy_policy_url: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-[#1D4ED8] focus:outline-none" />
                    <p className="text-[11px] text-gray-400 mt-1">{t('campaignDraft.leadForm.privacyNote')}</p>
                  </div>
                </div>
              </>
              )}
            </div>
            );
          })()}

          {/* ===== STEP Summary: Podsumowanie ===== */}
          {step === stepSummary && (() => {
            const campaignType = draft.campaign_type || 'single';
            const isReferral = campaignType === 'referral';
            const isMulti = hasAdGroups;
            const totalHeadlines = isMulti ? adGroupsState.reduce((s, ag) => s + (ag.headlines?.length || 0), 0) : headlines.filter(h => h.trim()).length;
            const totalDescriptions = isMulti ? adGroupsState.reduce((s, ag) => s + (ag.descriptions?.length || 0), 0) : descriptions.filter(d => d.trim()).length;
            const longTailCount = keywords.filter(kw => kw.text.split(' ').length >= 3).length;

            return (
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">{t('campaignDraft.summary.title')}</h3>
              <p className="text-sm text-gray-500 mb-5">
                {t('campaignDraft.summary.subtitle')}
              </p>

              <div className="space-y-3">
                {/* Campaign info */}
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">{t('campaignDraft.summary.campaign')}</p>
                  <p className="text-sm font-semibold text-gray-900">{campaignName}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
                    <p className="text-xs text-gray-500">{t('campaignDraft.summary.budgetLabel')} <span className="text-gray-700 font-medium">{t('campaignDraft.units.perDay', { amount: getActiveBudget(), cur: curSym })}</span></p>
                    <p className="text-xs text-gray-500">{t('campaignDraft.summary.typeLabel')} <span className="text-gray-700 font-medium">
                      {isReferral ? t('campaignDraft.summary.typeReferral') : isMulti ? t('campaignDraft.summary.typeMulti') : t('campaignDraft.summary.typeSearch')}
                    </span></p>
                    {draft.max_cpc_limit && (
                      <p className="text-xs text-gray-500">{t('campaignDraft.summary.maxCpcLabel', { cpc: draft.max_cpc_limit, cur: curSym })}</p>
                    )}
                  </div>
                </div>

                {/* Ad groups (multi-URL) */}
                {isMulti && (
                  <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-2">{t('campaignDraft.summary.adGroups', { count: adGroupsState.length })}</p>
                    <div className="space-y-2">
                      {adGroupsState.map((ag, i) => {
                        const agKwCount = keywords.filter(k => k.ad_group_name === ag.name).length;
                        return (
                          <div key={i} className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-1.5 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900">{ag.name}</p>
                              <p className="text-[11px] text-gray-400 truncate">{t('campaignDraft.summary.adGroupCounts', { kw: agKwCount, h: ag.headlines?.length || 0, d: ag.descriptions?.length || 0 })}</p>
                              {isReferral && ag.final_url && (
                                <p className="text-[10px] text-indigo-500 truncate mt-0.5">→ {ag.final_url.split('?')[0]}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Keywords or Audience */}
                {isMetaPlatform ? (
                  <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs text-gray-500">{t('campaignDraft.summary.audience')}</p>
                      {audienceSize && audienceSize.users_upper_bound > 0 && (
                        <span className="text-xs font-medium text-indigo-600">
                          {t('campaignDraft.summary.people', {
                            lower: audienceSize.users_lower_bound >= 1_000_000 ? `${(audienceSize.users_lower_bound / 1_000_000).toFixed(1)}M` : audienceSize.users_lower_bound >= 1_000 ? `${(audienceSize.users_lower_bound / 1_000).toFixed(0)}K` : audienceSize.users_lower_bound,
                            upper: audienceSize.users_upper_bound >= 1_000_000 ? `${(audienceSize.users_upper_bound / 1_000_000).toFixed(1)}M` : audienceSize.users_upper_bound >= 1_000 ? `${(audienceSize.users_upper_bound / 1_000).toFixed(0)}K` : audienceSize.users_upper_bound,
                          })}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-gray-900">
                      {t('campaignDraft.summary.demographics', {
                        min: audience.age_min || 18,
                        max: audience.age_max || 55,
                        gender: (audience.genders || ['all'])[0] === 'all' ? t('campaignDraft.summary.genderAll') : (audience.genders || [])[0] === 'male' ? t('campaignDraft.summary.genderMale') : t('campaignDraft.summary.genderFemale'),
                      })}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(audience.interests_data || audience.interests || []).slice(0, 6).map((int, i) => (
                        <span key={i} className="text-xs bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5 text-indigo-600">
                          {typeof int === 'string' ? int : int.name}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">{t('campaignDraft.summary.keywords')}</p>
                    <p className="text-sm font-semibold text-gray-900">{t('campaignDraft.summary.keywordsCount', { count: keywords.length })}</p>
                    {longTailCount > 0 && (
                      <p className="text-[11px] text-green-600 mt-0.5">{t('campaignDraft.summary.longTail', { count: longTailCount, percent: Math.round(longTailCount / keywords.length * 100) })}</p>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {keywords.slice(0, 8).map((kw, i) => (
                        <span key={i} className="text-xs bg-white border border-gray-200 rounded px-2 py-0.5 text-gray-600">
                          {kw.text}
                        </span>
                      ))}
                      {keywords.length > 8 && (
                        <span className="text-xs text-gray-400">{t('campaignDraft.summary.moreKeywords', { count: keywords.length - 8 })}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Ads */}
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">{t('campaignDraft.summary.ads')}</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {t('campaignDraft.summary.adsCount', { h: totalHeadlines, d: totalDescriptions })}
                    {isMulti && <span className="text-xs font-normal text-gray-400 ml-1">{t('campaignDraft.summary.inGroups', { count: adGroupsState.length })}</span>}
                  </p>
                  {!isMulti && finalUrl && (
                    <p className="text-xs text-gray-500 mt-1 truncate">{t('campaignDraft.summary.urlLabel', { url: finalUrl })}</p>
                  )}
                </div>

                {/* Creative images (Meta) */}
                {isMetaPlatform && creativeImages.length > 0 && (
                  <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">{t('campaignDraft.summary.creatives')}</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {t('campaignDraft.summary.creativesCount', { selected: creativeImages.filter(i => i.is_selected).length, total: creativeImages.length })}
                    </p>
                  </div>
                )}

                {/* Referral info */}
                {isReferral && (
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                    <p className="text-xs text-blue-600 font-medium mb-1">{t('campaignDraft.summary.referralTitle')}</p>
                    <p className="text-xs text-blue-500 lh-1.5">
                      {t('campaignDraft.summary.referralText')}
                    </p>
                  </div>
                )}

                {/* Cost estimate */}
                <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4">
                  <p className="text-xs text-indigo-600 mb-1">{t('campaignDraft.summary.costTitle')} {isMetaPlatform ? '(Meta Ads)' : '(Google Ads)'}</p>
                  <p className="text-2xl font-bold text-indigo-700">{formatMoney(estimatedMonthlyCost, campaign.currency, { decimals: 0 })}</p>
                  <p className="text-xs text-indigo-500 mt-0.5">
                    {t('campaignDraft.summary.costPerDay', { budget: getActiveBudget(), cur: curSym })}
                  </p>
                </div>
              </div>
            </div>
            );
          })()}

          {/* ===== Error state (publish failed) ===== */}
          {step === 'error' && (
            <div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-5">
                <div className="flex items-start gap-3">
                  <Warning className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">{t('campaignDraft.errorState.publishFailedTitle')}</p>
                    <p className="text-xs text-red-600 mt-1">
                      {campaign.ai_draft_data?.error_message || campaign.ai_draft_data?.error || campaign.draft_error || t('campaignDraft.errorState.publishFailedPlatform', { where: isMetaPlatform ? t('campaignDraft.platform.inMeta') : t('campaignDraft.platform.inGoogle') })}
                    </p>
                    {campaign.ai_draft_data?.error_detail && (
                      <p className="text-[11px] text-red-500 mt-1 break-words">{campaign.ai_draft_data.error_detail}</p>
                    )}
                  </div>
                </div>
              </div>

              <h3 className="text-base font-semibold text-gray-900 mb-4">{t('campaignDraft.errorState.summaryTitle')}</h3>

              <div className="space-y-3 mb-6">
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
                  <p className="text-xs text-gray-500">{t('campaignDraft.errorState.campaign')}</p>
                  <p className="text-sm font-semibold text-gray-900">{campaignName}</p>
                  <p className="text-xs text-gray-500 mt-1">{t('campaignDraft.errorState.budgetLine', { budget: getActiveBudget(), url: campaign.source_url, cur: curSym })}</p>
                </div>
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
                  <p className="text-xs text-gray-500">{t('campaignDraft.errorState.keywords')}</p>
                  <p className="text-sm text-gray-900">{t('campaignDraft.errorState.keywordsCount', { count: keywords.length })}</p>
                </div>
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
                  <p className="text-xs text-gray-500">{t('campaignDraft.errorState.ads')}</p>
                  <p className="text-sm text-gray-900">{t('campaignDraft.errorState.adsCount', { h: headlines.filter(h => h.trim()).length, d: descriptions.filter(d => d.trim()).length })}</p>
                </div>
              </div>

              {publishError && (
                <div className="bg-red-50 border border-red-100 rounded-lg p-3 mb-4">
                  <p className="text-xs text-red-600">{publishError}</p>
                </div>
              )}

              {/* Popraw ustawienia (adres, słowa, teksty reklam) przed ponowną publikacją. */}
              <button
                onClick={() => setStep(1)}
                disabled={publishing}
                className="w-full mb-2 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-50 transition disabled:opacity-50"
              >
                {t('campaignDraft.errorState.edit')}
              </button>
              <button
                onClick={handleRetryPublish}
                disabled={publishing}
                className="w-full bg-[#1D4ED8] text-white py-3 rounded-lg text-sm font-semibold hover:bg-[#1e40af] transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {publishing ? (
                  <>
                    <CircleNotch className="w-4 h-4 animate-spin" />
                    {t('campaignDraft.errorState.publishing')}
                  </>
                ) : (
                  <>
                    <Sparkle className="w-4 h-4" />
                    {t('campaignDraft.errorState.retry')}
                  </>
                )}
              </button>
            </div>
          )}

          {/* ===== STEP Activate: Aktywuj Droid ===== */}
          {step === stepActivate && (
            <div>
              {!publishing && !publishDone && (
                <>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">{t('campaignDraft.activate.title')}</h3>
                  <p className="text-sm text-gray-500 mb-6">
                    {isFreeAccount ? t('campaignDraft.activate.subtitleFree') : t('campaignDraft.activate.subtitlePaid')}
                  </p>

                  {isFreeAccount ? (
                    <div>
                      <div className="border border-green-200 bg-green-50 rounded-lg p-5 mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                            <Check className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{t('campaignDraft.activate.freeTitle')}</p>
                            <p className="text-xs text-green-600">{t('campaignDraft.activate.freeSubtitle')}</p>
                          </div>
                        </div>
                        <ul className="text-xs text-gray-600 space-y-1 mt-3">
                          <li className="flex items-start gap-1.5">
                            <Check className="w-3 h-3 text-green-500 mt-0.5 shrink-0" />
                            {t('campaignDraft.activate.freeFeature1')}
                          </li>
                          <li className="flex items-start gap-1.5">
                            <Check className="w-3 h-3 text-green-500 mt-0.5 shrink-0" />
                            {t('campaignDraft.activate.freeFeature2')}
                          </li>
                        </ul>
                      </div>
                      <button
                        onClick={handleActivateTrial}
                        disabled={publishing}
                        className="w-full bg-[#1D4ED8] text-white py-3 rounded-lg text-sm font-semibold hover:bg-[#1e40af] transition disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {publishing ? (
                          <><CircleNotch className="w-4 h-4 animate-spin" />{t('campaignDraft.activate.publishing')}</>
                        ) : (
                          <><Sparkle className="w-4 h-4" />{t('campaignDraft.activate.publishCampaign')}</>
                        )}
                      </button>
                    </div>
                  ) : true ? (
                    <div className="grid grid-cols-2 gap-4">
                      {/* Trial card */}
                      <div className={`border rounded-lg p-5 flex flex-col ${trialUsed ? 'border-gray-100 opacity-50' : 'border-gray-200'}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                            <Lightning className="w-4 h-4 text-indigo-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{t('campaignDraft.activate.trialTitle')}</p>
                            <p className="text-xs text-gray-500">{t('campaignDraft.activate.trialSubtitle')}</p>
                          </div>
                        </div>
                        <ul className="text-xs text-gray-500 space-y-1.5 mb-5 flex-1">
                          <li className="flex items-start gap-1.5">
                            <Check className="w-3 h-3 text-green-500 mt-0.5 shrink-0" />
                            {t('campaignDraft.activate.trialFeature1')}
                          </li>
                          <li className="flex items-start gap-1.5">
                            <Check className="w-3 h-3 text-green-500 mt-0.5 shrink-0" />
                            {t('campaignDraft.activate.trialFeature2')}
                          </li>
                          <li className="flex items-start gap-1.5">
                            <Check className="w-3 h-3 text-green-500 mt-0.5 shrink-0" />
                            {t('campaignDraft.activate.trialFeature3')}
                          </li>
                        </ul>
                        {trialUsed ? (
                          <p className="text-xs text-center text-gray-400">{t('campaignDraft.activate.trialUsed')}</p>
                        ) : (
                          <button
                            onClick={handleActivateTrial}
                            disabled={publishing}
                            className="w-full bg-[#1D4ED8] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-[#1e40af] transition disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            <Lightning className="w-4 h-4" />
                            {t('campaignDraft.activate.activateTrial')}
                          </button>
                        )}
                      </div>

                      {/* Subscription card */}
                      <div className="border border-gray-200 rounded-lg p-5 flex flex-col">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                            <CreditCard className="w-4 h-4 text-indigo-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{t('campaignDraft.activate.subscriptionTitle')}</p>
                            <p className="text-xs text-gray-500">{subscriptionPriceLabel}</p>
                          </div>
                        </div>
                        <ul className="text-xs text-gray-500 space-y-1.5 mb-5 flex-1">
                          <li className="flex items-start gap-1.5">
                            <Check className="w-3 h-3 text-green-500 mt-0.5 shrink-0" />
                            {t('campaignDraft.activate.subFeature1')}
                          </li>
                          <li className="flex items-start gap-1.5">
                            <Check className="w-3 h-3 text-green-500 mt-0.5 shrink-0" />
                            {t('campaignDraft.activate.subFeature2')}
                          </li>
                          <li className="flex items-start gap-1.5">
                            <Check className="w-3 h-3 text-green-500 mt-0.5 shrink-0" />
                            {t('campaignDraft.activate.subFeature3')}
                          </li>
                        </ul>
                        <button
                          onClick={handleSubscribe}
                          disabled={publishing}
                          className="w-full border border-indigo-600 text-indigo-600 py-2.5 rounded-lg text-sm font-semibold hover:bg-indigo-50 transition disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          <CreditCard className="w-4 h-4" />
                          {t('campaignDraft.activate.goToPayment')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="text-base font-semibold text-gray-900">{t('campaignDraft.activate.subscriptionTitle')}</p>
                          <p className="text-sm text-gray-500">{subscriptionPriceLabel}</p>
                        </div>
                      </div>
                      <ul className="text-sm text-gray-500 space-y-2 mb-6">
                        <li className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                          {t('campaignDraft.activate.subFeature1')}
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                          {t('campaignDraft.activate.subFeature2')}
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                          {t('campaignDraft.activate.subFeature3')}
                        </li>
                      </ul>
                      <button
                        onClick={handleSubscribe}
                        disabled={publishing}
                        className="w-full bg-[#1D4ED8] text-white py-3 rounded-lg text-sm font-semibold hover:bg-[#1e40af] transition disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        <CreditCard className="w-4 h-4" />
                        {t('campaignDraft.activate.goToPayment')}
                      </button>
                    </div>
                  )}

                  <p className="text-xs text-gray-400 mt-4 leading-relaxed">
                    {!trialUsed
                      ? t('campaignDraft.activate.noteTrial')
                      : t('campaignDraft.activate.notePaid')}
                  </p>

                  {publishError && (
                    <div className="mt-4 bg-red-50 border border-red-100 rounded-lg p-3">
                      <p className="text-xs text-red-600">{publishError}</p>
                    </div>
                  )}
                </>
              )}

              {/* Publishing progress */}
              {publishing && !publishDone && (
                <div className="py-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <CircleNotch className="w-5 h-5 text-indigo-600 animate-spin" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">{t('campaignDraft.activate.publishingTitle')}</h4>
                      <p className="text-xs text-gray-500">{t('campaignDraft.activate.publishingSubtitle')}</p>
                    </div>
                  </div>

                  <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                      {t('campaignDraft.activate.campaignLine', { name: campaignName })}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                      {t('campaignDraft.activate.budgetLine', { budget: getActiveBudget(), cur: curSym })}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                      {t('campaignDraft.activate.keywordsLine', { count: keywords.length })}
                    </div>
                  </div>
                </div>
              )}

              {/* Success */}
              {publishDone && (
                <div className="py-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">{t('campaignDraft.activate.successTitle')}</h4>
                      <p className="text-xs text-gray-500">{t('campaignDraft.activate.successSubtitle')}</p>
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                    <p className="text-xs text-green-700">
                      {t('campaignDraft.activate.campaignCreated', { name: campaignName, where: isMetaPlatform ? t('campaignDraft.platform.inMeta') : t('campaignDraft.platform.inGoogle') })}
                    </p>
                  </div>

                  {publishWarnings.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                      <p className="text-xs text-red-700 font-medium mb-1">{t('campaignDraft.activate.warningsTitle')}</p>
                      <ul className="list-disc pl-4 space-y-1">
                        {publishWarnings.map((w, i) => (
                          <li key={i} className="text-xs text-red-700">{w}</li>
                        ))}
                      </ul>
                      <p className="text-xs text-red-600 mt-2">{t('campaignDraft.activate.warningsHint')}</p>
                    </div>
                  )}

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
                    <p className="text-xs text-amber-700 font-medium mb-1">{t('campaignDraft.activate.pausedTitle')}</p>
                    <p className="text-xs text-amber-600">
                      {isMetaPlatform
                        ? t('campaignDraft.activate.pausedMeta')
                        : t('campaignDraft.activate.pausedGoogle')}
                      {' '}{t('campaignDraft.activate.pausedSuffix')}
                    </p>
                  </div>

                  {/* Conversion tracking prompt (Google Ads): most users never set
                      up tags, so the campaign has no feedback. Offer it here. */}
                  {!isMetaPlatform && (
                    <div className={`border rounded-lg p-4 mb-6 ${conversionDone ? 'bg-green-50 border-green-200' : 'bg-indigo-50 border-indigo-200'}`}>
                      {conversionDone ? (
                        <p className="text-xs text-green-700 flex items-center gap-2">
                          <Check className="w-4 h-4" /> {t('campaignDraft.activate.conversionDone')}
                        </p>
                      ) : (
                        <>
                          <p className="text-sm font-semibold text-gray-900 mb-1">{t('campaignDraft.activate.conversionTitle')}</p>
                          <p className="text-xs text-gray-600 mb-3">
                            {t('campaignDraft.activate.conversionText')}
                          </p>
                          <button
                            onClick={() => setShowConversionSetup(true)}
                            className="bg-[#1D4ED8] text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-[#1e40af] transition flex items-center gap-1.5"
                          >
                            <Lightning className="w-3.5 h-3.5" /> {t('campaignDraft.activate.conversionButton')}
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  <button
                    onClick={handleClose}
                    className="w-full bg-[#1D4ED8] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-[#1e40af] transition"
                  >
                    {!isMetaPlatform && !conversionDone ? t('campaignDraft.activate.skipClose') : t('campaignDraft.activate.close')}
                  </button>

                  <ConversionSetup
                    campaignId={campaign.id}
                    isSpa={!!campaign.ai_draft_data?.site_is_spa}
                    opened={showConversionSetup}
                    onClose={() => setShowConversionSetup(false)}
                    onCreated={() => setConversionDone(true)}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Keyword add form - fixed above footer on step 3 (Google Ads only) */}
        {step === 3 && !isMetaPlatform && !publishDone && !publishing && (
          <div className="border-t border-gray-200 px-6 py-3 bg-gray-50">
            {hasAdGroups && (
              <div className="mb-2">
                <select
                  value={addToGroup}
                  onChange={e => setAddToGroup(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  {draft.ad_groups.map((ag, i) => (
                    <option key={i} value={ag.name}>{ag.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={newKeyword}
                onChange={e => setNewKeyword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addCustomKeyword()}
                placeholder={hasAdGroups ? t('campaignDraft.keywordFooter.addToGroupPlaceholder', { group: addToGroup }) : t('campaignDraft.keywordFooter.addPlaceholder')}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <button
                onClick={addCustomKeyword}
                disabled={!newKeyword.trim()}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition disabled:opacity-50 flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" />
                {t('campaignDraft.keywordFooter.add')}
              </button>
            </div>
          </div>
        )}

        {/* Fixed footer with navigation */}
        {!publishDone && !publishing && step !== 'error' && (
          <div className="border-t border-gray-200 px-6 py-3 bg-white flex items-center justify-between">
            {step > 1 ? (
              <button
                onClick={() => setStep(step - 1)}
                className="text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition flex items-center gap-1"
              >
                <CaretLeft className="w-4 h-4" /> {t('campaignDraft.nav.back')}
              </button>
            ) : (
              <div />
            )}
            {step < totalSteps ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={step === 1 && !campaignName.trim()}
                className="bg-[#1D4ED8] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#1e40af] transition disabled:opacity-50 flex items-center gap-1"
              >
                {t('campaignDraft.nav.next')} <CaretRight className="w-4 h-4" />
              </button>
            ) : (
              <div />
            )}
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative bg-white rounded-lg border border-gray-200 shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('campaignDraft.deleteModal.title')}</h3>
            <p className="text-xs text-gray-500 mb-4">
              {t('campaignDraft.deleteModal.text', { name: campaignName })}
            </p>
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-3 py-1.5 rounded-md border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition"
              >
                {t('campaignDraft.deleteModal.cancel')}
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-3 py-1.5 rounded-md bg-red-600 text-sm text-white hover:bg-red-700 transition disabled:opacity-50"
              >
                {deleting ? t('campaignDraft.deleteModal.deleting') : t('campaignDraft.deleteModal.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
